/**
 * Meili Travel Planner — v2 frontend
 *
 * Experience pipeline:
 *   FORM → LOADING (narrated SVG map) → STREAMING (itinerary shell, cards
 *   pop in as SSE content events arrive) → COMPLETE (refine chips, share
 *   permalink, connectivity cards).
 *
 * Protocol: sends stream_version: 2. Against a v2 worker, content events
 * arrive progressively during generation; against the legacy worker they
 * arrive in a burst at the end — both render correctly through the same
 * handlers. Itineraries persist server-side; ?trip=<id> restores/shares.
 *
 * No dependencies, no build step. html2pdf is lazy-loaded on demand.
 */
(function() {
  'use strict';

  // ==========================================================================
  // Configuration (tenant-parametrized via data-* attributes)
  // ==========================================================================
  const plannerEl = document.querySelector('.meili-planner');
  if (!plannerEl) return;
  const plannerData = plannerEl.dataset || {};

  const API_BASE = plannerData.apiBase || 'https://meili-api.isaaq990.workers.dev';
  const COUNTRIES_ENDPOINT = API_BASE + '/v1/countries';
  const PLAN_ENDPOINT = API_BASE + '/v1/plan';
  const REFINE_ENDPOINT = API_BASE + '/v1/refine';
  const ITINERARY_ENDPOINT = API_BASE + '/v1/itinerary/';

  const BRAND_NAME = plannerData.brandName || 'Latam Travellers';
  const SITE_DOMAIN = plannerData.siteDomain || 'latamtravellers.com';
  const TWITTER_HANDLE = plannerData.twitterHandle || 'LATAMTravellers';
  const REGION_MAP = plannerData.regionMap || 'south-america';
  const LOCALE = plannerData.locale === 'es' ? 'es' : 'en';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ==========================================================================
  // i18n (UI strings only; itinerary content localized server-side)
  // ==========================================================================
  const STR = LOCALE === 'es' ? {
    nights: 'noches', night: 'noche', days: 'días', day: 'Día',
    dayByDay: 'Día a día', highlights: 'Lo esencial',
    stayConnected: 'Mantente conectado en', regionalTitle: 'Una eSIM para toda tu ruta',
    sheetTitle: 'eSIMs para tu viaje',
    coversCountries: 'cubre los :n países de tu viaje',
    daysThere: ':n días allí', recommended: 'recomendado',
    addToCart: 'Añadir al carrito', added: '¡Añadido!', adding: 'Añadiendo…',
    viewEsims: 'Ver eSIMs', instantQr: 'QR al instante por email · Sin costos de roaming',
    goodToKnow: 'Bueno saber', nextStop: 'Siguiente',
    planAnother: 'Planear otro viaje', share: 'Compartir', save: 'Guardar',
    downloadPdf: 'Descargar PDF', printIt: 'Imprimir itinerario', printSub: 'Diálogo de impresión',
    pdfSub: 'Formato listo para imprimir', shareVia: 'Comparte tu itinerario',
    copyLink: 'Copiar enlace', copied: '¡Copiado!',
    refineTitle: '¿Ajustamos algo?', refinePlaceholder: 'Pide un cambio… ej. "más playas"',
    refineSend: 'Ajustar', revising: 'Meili está ajustando tu plan…',
    refineChips: ['Ritmo más relajado', 'Más gastronomía', 'Más naturaleza', 'Más económico', 'Más vida nocturna'],
    stages: [
      { t: 'Entendiendo tu viaje…', s: 'Analizando tus preferencias' },
      { t: 'Trazando tu ruta…', s: 'Buscando las mejores conexiones' },
      { t: 'Sumando datos locales…', s: 'Experiencias reales de viajeros' },
      { t: 'Puliendo los detalles…', s: 'Tu itinerario está casi listo' },
    ],
    stageLabels: ['Entender', 'Trazar', 'Sumar', 'Pulir'],
    writingPlan: 'Meili está escribiendo tu plan…',
    plannedOn: 'Planeado el', linkExpired: 'Ese enlace de itinerario expiró — planea uno nuevo abajo.',
    genError: 'Ocurrió un error al generar tu itinerario. Por favor inténtalo de nuevo.',
    rateLimited: 'Demasiadas solicitudes seguidas — espera un momento e inténtalo de nuevo.',
    interrupted: 'Se interrumpió la conexión. Esto es lo que Meili alcanzó a planear.',
    tryAgain: 'Intentar de nuevo',
    disclaimerLoading: 'Impulsado por IA. La asistencia se basa en experiencias públicas de viajeros; no es asesoría, garantía ni reserva.',
    disclaimerOutput: 'Impulsado por IA. Meili sugiere planes basados en experiencias de viajeros y no es asesoría profesional de viaje. Verifica las condiciones actuales de forma independiente.',
    poweredFooter: 'Rutas, transporte, tiempos — como un local',
  } : {
    nights: 'nights', night: 'night', days: 'days', day: 'Day',
    dayByDay: 'Day by day', highlights: 'Highlights',
    stayConnected: 'Stay connected in', regionalTitle: 'One eSIM for your whole route',
    sheetTitle: 'eSIMs for this trip',
    coversCountries: 'covers all :n countries on your trip',
    daysThere: ':n days there', recommended: 'recommended',
    addToCart: 'Add to Cart', added: 'Added!', adding: 'Adding…',
    viewEsims: 'View eSIMs', instantQr: 'Instant QR by email · No roaming fees',
    goodToKnow: 'Good to know', nextStop: 'Next',
    planAnother: 'Plan Another Trip', share: 'Share', save: 'Save',
    downloadPdf: 'Download PDF', printIt: 'Print Itinerary', printSub: 'Browser print dialog',
    pdfSub: 'Print-ready format', shareVia: 'Share your itinerary',
    copyLink: 'Copy link', copied: 'Copied!',
    refineTitle: 'Want to tweak anything?', refinePlaceholder: 'Ask for a change… e.g. "more beach time"',
    refineSend: 'Refine', revising: 'Meili is revising your plan…',
    refineChips: ['Slower pace', 'More food', 'More nature', 'Budget focus', 'More nightlife'],
    stages: [
      { t: 'Understanding your trip…', s: 'Analyzing your preferences' },
      { t: 'Mapping your route…', s: 'Finding the best connections' },
      { t: 'Adding local insight…', s: 'Real traveller experiences' },
      { t: 'Polishing the details…', s: 'Your itinerary is almost ready' },
    ],
    stageLabels: ['Understand', 'Map', 'Insight', 'Polish'],
    writingPlan: 'Meili is writing your plan…',
    plannedOn: 'Planned on', linkExpired: 'That itinerary link has expired — plan a new trip below.',
    genError: 'An error occurred while generating your itinerary. Please try again.',
    rateLimited: 'Too many requests in a row — give it a moment and try again.',
    interrupted: 'The connection was interrupted. Here is what Meili planned so far.',
    tryAgain: 'Try Again',
    disclaimerLoading: 'Powered by AI. Planning assistance is based on publicly shared traveller experiences, not advice, guarantees, or bookings.',
    disclaimerOutput: 'Powered by AI. Meili provides planning suggestions based on traveller experiences and is not professional travel advice. Always verify current conditions independently.',
    poweredFooter: 'Routes, transport, timing — built like a local',
  };

  // ==========================================================================
  // Analytics (Shopify analytics bus → Custom Pixel → GA4)
  // ==========================================================================
  function meiliTrack(name, params) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('meili_' + name, params || {});
      }
    } catch (e) { /* analytics must never break the planner */ }
  }

  // ==========================================================================
  // State
  // ==========================================================================
  let countriesData = [];
  let selectedCountries = [];
  let isSubmitting = false;

  // Current trip render state
  const trip = {
    id: null,            // itinerary_id from the worker
    title: '',
    totalDays: 0,
    style: '',
    focus: [],
    route: [],           // [{city, nights}]
    segments: [],        // normalized day segments
    notes: [],           // meili notes
    esim: null,          // esim_recommendations payload
    request: null,       // last request payload (for share text)
    createdAt: null,
    partial: false,
  };
  let cityToCountry = {};   // lowercase city -> country name
  let countryCodeByName = {}; // country name -> ISO code
  let segmentCountrySeen = {}; // country -> true once its connectivity card is placed
  let streamPhase = 'idle'; // idle | loading | streaming | done | error

  // ==========================================================================
  // DOM
  // ==========================================================================
  const form = document.getElementById('meili-form');
  const submitBtn = document.getElementById('submit-btn');
  const submitText = submitBtn && submitBtn.querySelector('.meili-form__submit-text');
  const submitLoading = submitBtn && submitBtn.querySelector('.meili-form__submit-loading');
  const resultsSection = document.getElementById('meili-results');
  const resultsContent = document.getElementById('results-content');
  const errorSection = document.getElementById('meili-error');
  const errorMessage = document.getElementById('error-message');
  const retryBtn = document.getElementById('retry-btn');
  const plannerHeader = document.querySelector('.meili-planner__header');

  const countriesContainer = document.getElementById('countries-container');
  const countriesSelected = document.getElementById('countries-selected');
  const countriesDropdown = document.getElementById('countries-dropdown');
  const countriesOptions = document.getElementById('countries-options');
  const countriesSearch = document.getElementById('countries-search');
  const countriesInput = document.getElementById('countries');

  const startingCountrySelect = document.getElementById('starting_country');
  const startingCitySelect = document.getElementById('starting_city');
  const prioritiesGroup = document.getElementById('priorities-group');
  const prioritiesCounter = document.getElementById('priorities-counter');
  const priorityCheckboxes = prioritiesGroup ? prioritiesGroup.querySelectorAll('input[type="checkbox"]') : [];

  // ==========================================================================
  // Utilities
  // ==========================================================================
  function escapeHtml(text) {
    if (text === null || text === undefined) return '';
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  }

  // For values placed inside HTML attributes (also escapes quotes).
  function escapeAttr(text) {
    return escapeHtml(text).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function el(html) {
    const t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstElementChild;
  }

  function pluralNights(n) {
    return n === 1 ? STR.night : STR.nights;
  }

  const COUNTRY_FLAGS = {
    AR: '🇦🇷', BO: '🇧🇴', BR: '🇧🇷', CL: '🇨🇱', CO: '🇨🇴', CR: '🇨🇷', CU: '🇨🇺',
    DO: '🇩🇴', EC: '🇪🇨', SV: '🇸🇻', GT: '🇬🇹', HN: '🇭🇳', MX: '🇲🇽', NI: '🇳🇮',
    PA: '🇵🇦', PY: '🇵🇾', PE: '🇵🇪', PR: '🇵🇷', UY: '🇺🇾', VE: '🇻🇪',
    TH: '🇹🇭', VN: '🇻🇳', ID: '🇮🇩', MY: '🇲🇾', SG: '🇸🇬', PH: '🇵🇭', KH: '🇰🇭',
    LA: '🇱🇦', LK: '🇱🇰', NP: '🇳🇵', MV: '🇲🇻', IN: '🇮🇳', JP: '🇯🇵', KR: '🇰🇷', TW: '🇹🇼',
  };
  function flagFor(code) { return COUNTRY_FLAGS[(code || '').toUpperCase()] || '🌍'; }

  // ==========================================================================
  // Icons
  // ==========================================================================
  const ICONS = {
    chevronRight: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>',
    chevronDown: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>',
    clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    bus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 6v6M16 6v6M2 12h20M6 18h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>',
    plane: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>',
    boat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 20a2.4 2.4 0 002 1 2.4 2.4 0 002-1 2.4 2.4 0 012-1 2.4 2.4 0 012 1 2.4 2.4 0 002 1 2.4 2.4 0 002-1 2.4 2.4 0 012-1 2.4 2.4 0 012 1 2.4 2.4 0 002 1 2.4 2.4 0 002-1"/><path d="M4 18l-1-5h18l-2 4"/><path d="M5 13V7h8l4 6"/><path d="M8 7V3h3v4"/></svg>',
    sun: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>',
    moon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>',
    coffee: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8h1a4 4 0 010 8h-1M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>',
    mapPin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>',
    star: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>',
    alert: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
    cart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>',
    download: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>',
    share: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>',
    x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    link: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>',
    fileText: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>',
    sparkle: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5L12 2z"/></svg>',
    arrowLeft: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>',
    wifi: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12.55a11 11 0 0114.08 0"/><path d="M1.42 9a16 16 0 0121.16 0"/><path d="M8.53 16.11a6 6 0 016.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>',
  };

  function transportIcon(mode) {
    const m = (mode || '').toLowerCase();
    if (m.indexOf('flight') > -1 || m.indexOf('plane') > -1 || m.indexOf('vuelo') > -1 || m.indexOf('avi') > -1) return ICONS.plane;
    if (m.indexOf('boat') > -1 || m.indexOf('ferry') > -1 || m.indexOf('barco') > -1) return ICONS.boat;
    return ICONS.bus;
  }

  function dayIcon(type) {
    switch (type) {
      case 'arrival': return ICONS.plane;
      case 'explore': return ICONS.sun;
      case 'rest': return ICONS.moon;
      case 'flexible': return ICONS.coffee;
      default: return ICONS.mapPin;
    }
  }

  // ==========================================================================
  // Loading map (SVG). LATAM keeps the continent; other tenants get the
  // brand-neutral route. Country paths carry data-cc so the traveller's
  // selected countries light up while Meili plans.
  // ==========================================================================
  const SA_PATHS = [
    ['AR', 'M291.6,648.91l-2.66,0.25l-1.43,-1.73l-1.69,-0.13l-3,0l0,-10.57l1.08,2.15l1.4,3.53l3.65,2.87l3.93,1.21L291.6,648.91zM293.1,526.47l1.65,2.18l1.09,-2.43l3.2,0.12l0.45,0.64l5.15,4.94l2.29,0.46l3.43,2.26l2.89,1.2l0.4,1.36l-2.76,4.73l2.83,0.85l3.15,0.48l2.22,-0.5l2.54,-2.4l0.46,-2.74l1.39,-0.59l1.41,1.79l-0.06,2.49l-2.36,1.73l-1.88,1.28l-3.16,3.08l-3.74,4.37l-0.7,2.59l-0.75,3.37l0.03,3.3l-0.61,0.74l-0.22,2.17l-0.19,1.76l3.56,2.91l-0.38,2.37l1.75,1.51l-0.14,1.7l-2.69,4.52l-4.16,1.91l-5.62,0.75l-3.08,-0.36l0.59,2.15l-0.57,2.72l0.52,1.85l-1.68,1.3l-2.87,0.51l-2.7,-1.35l-1.08,0.97l0.39,3.71l1.89,1.14l1.54,-1.19l0.84,1.96l-2.58,1.18l-2.25,2.38l-0.41,3.91l-0.66,2.11l-2.65,0.01l-2.2,2.04l-0.8,3.01l2.76,2.98l2.68,0.83l-0.96,3.73l-3.31,2.38l-1.82,5.03l-2.56,1.72l-1.15,2.06l0.91,4.64l1.87,2.63l-1.18,-0.23l-2.6,-0.71l-6.78,-0.61l-1.16,-2.63l0.05,-3.33l-1.87,0.28l-0.99,-1.6l-0.25,-4.6l2.15,-1.88l0.89,-2.68l-0.33,-2.11l1.49,-3.52l1.02,-5.35l-0.3,-2.33l1.22,-0.75l-0.3,-1.48l-1.3,-0.78l0.92,-1.63l-1.27,-1.46l-0.65,-4.4l1.13,-0.77l-0.47,-4.54l0.66,-3.75l0.75,-3.22l1.68,-1.3l-0.85,-3.46l-0.01,-3.22l2.12,-2.26l-0.06,-2.87l1.6,-3.31l0.01,-3.09l-0.73,-0.61l-1.29,-5.69l1.73,-3.34l-0.27,-3.11l1,-2.9l1.84,-2.96l1.98,-1.95l-0.84,-1.23l0.59,-1l-0.09,-5.14l3.05,-1.51l0.96,-3.16l-0.34,-0.76l2.34,-2.72L293.1,526.47z'],
    ['BO', 'M299.04,526.35L295.84,526.22L294.75,528.65L293.1,526.47L289.43,525.74L287.1,528.46L285.07,528.87L283.97,524.72L282.47,521.38L283.35,518.51L281.88,517.26L281.51,515.14L280.13,513.14L281.9,510L280.69,507.56L281.34,506.59L280.83,505.52L281.93,504.08L281.99,501.64L282.12,499.62L282.73,498.66L280.3,494.08L282.39,494.32L283.83,494.25L284.46,493.4L286.91,492.25L288.38,491.19L292.05,490.71L291.76,492.83L292.1,493.92L291.87,495.82L294.92,498.37L298.06,498.84L299.16,499.91L301.06,500.48L302.22,501.31L303.98,501.28L305.61,502.13L305.73,503.79L306.28,504.63L306.32,505.88L305.5,505.92L306.58,509.29L311.95,509.41L311.54,511.09L311.84,512.24L313.37,513.06L314.04,514.88L313.54,517.2L312.77,518.49L313.04,520.18L312.16,520.79L312.12,519.88L309.5,518.37L306.9,518.32L302.01,519.18L300.67,521.8L300.6,523.4L299.49,526.99z'],
    ['BR', 'M313.68,551.79L317.42,547.42L320.59,544.34L322.47,543.06L324.83,541.33L324.89,538.84L323.48,537.05L322.09,537.64L322.64,535.86L323.02,534.04L323.02,532.36L322.01,531.81L320.96,532.3L319.92,532.17L319.59,530.99L319.33,528.22L318.8,527.32L316.91,526.5L315.77,527.09L312.81,526.51L312.99,522.45L312.16,520.79L313.04,520.18L312.77,518.49L313.54,517.2L314.04,514.88L313.37,513.06L311.84,512.24L311.54,511.09L311.95,509.41L306.58,509.29L305.5,505.92L306.32,505.88L306.28,504.63L305.73,503.79L305.61,502.13L303.98,501.28L302.22,501.31L301.06,500.48L299.16,499.91L298.06,498.84L294.92,498.37L291.87,495.82L292.1,493.92L291.76,492.83L292.05,490.71L288.38,491.19L286.91,492.25L284.46,493.4L283.83,494.25L282.39,494.32L280.3,494.08L278.72,494.57L277.44,494.24L277.63,489.94L275.33,491.6L272.86,491.53L271.8,490.02L269.94,489.86L270.53,488.65L268.97,486.93L267.8,484.4L268.54,483.89L268.54,482.7L270.24,481.89L269.96,480.38L270.67,479.4L270.88,478.1L274.08,476.19L276.38,475.66L276.75,475.24L279.28,475.37L280.54,467.72L280.61,466.51L280.17,464.92L278.93,463.9L278.94,461.88L280.52,461.42L281.08,461.71L281.17,460.64L279.53,460.35L279.5,458.61L284.96,458.67L285.89,457.71L286.67,458.59L287.21,460.24L287.74,459.89L289.29,461.37L291.47,461.19L292.01,460.33L294.09,459.68L295.25,459.23L295.57,458.05L297.58,457.25L297.42,456.67L295.05,456.43L294.66,454.67L294.77,452.8L293.52,452.08L294.04,451.82L296.12,452.18L298.35,452.88L299.16,452.22L301.17,451.78L304.31,450.74L305.34,449.67L304.96,448.88L306.42,448.76L307.08,449.4L306.71,450.63L307.67,451.05L308.32,452.35L307.54,453.33L307.09,455.71L307.81,457.12L308.01,458.41L309.74,459.71L311.12,459.85L311.43,459.31L312.31,459.19L313.58,458.7L314.49,457.96L316.04,458.19L316.72,458.09L318.25,458.32L318.5,457.75L318.03,457.2L318.31,456.39L319.44,456.64L320.77,456.35L322.37,456.94L323.6,457.52L324.47,456.76L325.09,456.88L325.48,457.67L326.82,457.47L327.89,456.41L328.75,454.35L330.41,451.8L331.37,451.67L332.06,453.21L333.63,458.09L335.13,458.55L335.21,460.47L333.1,462.76L333.97,463.6L338.93,464.04L339.03,466.83L341.16,465L344.69,466.01L349.34,467.71L350.71,469.34L350.25,470.88L353.51,470.02L358.97,471.5L363.16,471.39L367.3,473.7L370.88,476.83L373.04,477.63L375.44,477.75L376.46,478.63L377.41,482.2L377.88,483.89L376.76,488.55L375.33,490.39L371.38,494.33L369.59,497.54L367.52,500.02L366.82,500.08L366.03,502.18L366.23,507.58L365.45,512.06L365.15,513.99L364.27,515.14L363.77,519.08L360.93,522.96L360.45,526.05L358.18,527.36L357.52,529.17L354.48,529.16L350.07,530.33L348.09,531.68L344.95,532.57L341.65,535.01L339.28,538.07L338.87,540.39L339.34,542.12L338.81,545.3L338.18,546.85L336.22,548.6L333.11,554.28L330.64,556.87L328.73,558.41L327.46,561.57L325.6,563.48L324.82,561.58L326.06,560.01L324.44,557.76L322.24,555.94L319.35,553.86L318.31,553.95L315.5,551.45z'],
    ['CL', 'M282.81,636.73l0,10.57l3,0l1.69,0.13l-0.93,1.98l-2.4,1.53l-1.38,-0.16l-1.66,-0.4l-2.04,-1.48l-2.94,-0.71l-3.53,-2.71l-2.86,-2.57l-3.86,-5.25l2.31,0.97l3.94,3.13l3.72,1.7l1.45,-2.17l0.91,-3.2l2.58,-1.91L282.81,636.73zM283.97,524.72l1.1,4.15l2.02,-0.41l0.34,0.76l-0.96,3.16l-3.05,1.51l0.09,5.14l-0.59,1l0.84,1.23l-1.98,1.95l-1.84,2.96l-1,2.9l0.27,3.11l-1.73,3.34l1.29,5.69l0.73,0.61l-0.01,3.09l-1.6,3.31l0.06,2.87l-2.12,2.26l0.01,3.22l0.85,3.46l-1.68,1.3l-0.75,3.22l-0.66,3.75l0.47,4.54l-1.13,0.77l0.65,4.4l1.27,1.46l-0.92,1.63l1.3,0.78l0.3,1.48l-1.22,0.75l0.3,2.33l-1.02,5.35l-1.49,3.52l0.33,2.11l-0.89,2.68l-2.15,1.88l0.25,4.6l0.99,1.6l1.87,-0.28l-0.05,3.33l1.16,2.63l6.78,0.61l2.6,0.71l-2.49,-0.03l-1.35,1.13l-2.53,1.67l-0.45,4.38l-1.19,0.11l-3.16,-1.54l-3.21,-3.25l0,0l-3.49,-2.63l-0.88,-2.87l0.79,-2.62l-1.41,-2.94l-0.36,-7.34l1.19,-4.03l2.96,-3.19l-4.26,-1.19l2.67,-3.57l0.95,-6.56l3.12,1.37l1.46,-7.97l-1.88,-1l-0.88,4.75l-1.77,-0.54l0.88,-5.42l0.96,-6.84l1.29,-2.48l-0.81,-3.5l-0.23,-3.98l1.18,-0.11l1.72,-5.6l1.94,-5.43l1.19,-4.97l-0.65,-4.91l0.84,-2.67l-0.34,-3.96l1.64,-3.87l0.51,-6.04l0.9,-6.37l0.88,-6.75l-0.21,-4.87l-0.58,-4.15l1.44,-0.75l0.75,-1.5l1.37,1.99l0.37,2.12l1.47,1.25l-0.88,2.87L283.97,524.72z'],
    ['CO', 'M263.92,463.81L262.72,463.15L261.34,462.23L260.54,462.67L258.16,462.28L257.48,461.08L256.96,461.13L254.15,459.54L253.77,458.67L254.82,458.46L254.7,457.07L255.35,456.06L256.74,455.87L257.93,454.12L259,452.66L257.96,451.99L258.49,450.37L257.86,447.81L258.46,447.08L258.02,444.71L256.88,443.21L257.24,441.85L258.15,442.05L258.68,441.21L258.03,439.56L258.37,439.14L259.81,439.23L261.92,437.26L263.07,436.96L263.1,436.03L263.62,433.64L265.23,432.32L266.99,432.27L267.21,431.68L269.41,431.91L271.62,430.48L272.71,429.84L274.06,428.47L275.06,428.64L275.79,429.39L275.25,430.35L273.45,430.83L272.74,432.25L271.65,433.06L270.84,434.12L270.49,436.13L269.72,437.79L271.16,437.97L271.52,439.27L272.14,439.89L272.36,441.02L272.03,442.06L272.13,442.65L272.82,442.88L273.49,443.86L277.09,443.59L278.72,443.95L280.7,446.36L281.83,446.06L283.85,446.21L285.45,445.89L286.44,446.38L285.93,447.88L285.31,448.82L285.09,450.83L285.65,452.68L286.45,453.51L286.54,454.14L285.12,455.53L286.14,456.14L286.89,457.12L287.74,459.89L287.21,460.24L286.67,458.59L285.89,457.71L284.96,458.67L279.5,458.61L279.53,460.35L281.17,460.64L281.08,461.71L280.52,461.42L278.94,461.88L278.93,463.9L280.17,464.92L280.61,466.51L280.54,467.72L279.28,475.37L277.88,473.88L277.04,473.82L278.85,470.98L276.7,469.67L275.02,469.91L274.01,469.43L272.46,470.17L270.37,469.82L268.72,466.9L267.42,466.18L266.53,464.86L264.67,463.54z'],
    ['EC', 'M250.1,472.87L251.59,470.79L250.98,469.57L249.91,470.87L248.23,469.64L248.8,468.86L248.33,466.33L249.31,465.91L249.83,464.18L250.89,462.38L250.69,461.25L252.23,460.65L254.15,459.54L256.96,461.13L257.48,461.08L258.16,462.28L260.54,462.67L261.34,462.23L262.72,463.15L263.92,463.81L264.31,465.92L263.44,467.73L260.38,470.65L257.01,471.75L255.29,474.18L254.76,476.06L253.17,477.21L252,475.8L250.86,475.5L249.7,475.72L249.63,474.7L250.43,474.04z'],
    ['PE', 'M280.13,513.14L279.38,514.65L277.94,515.39L275.13,513.71L274.88,512.51L269.33,509.59L264.3,506.42L262.13,504.64L260.97,502.27L261.43,501.44L259.06,497.69L256.29,492.45L253.65,486.83L252.5,485.54L251.62,483.48L249.44,481.64L247.44,480.51L248.35,479.26L246.99,476.59L247.86,474.64L250.1,472.87L250.43,474.04L249.63,474.7L249.7,475.72L250.86,475.5L252,475.8L253.17,477.21L254.76,476.06L255.29,474.18L257.01,471.75L260.38,470.65L263.44,467.73L264.31,465.92L263.92,463.81L264.67,463.54L266.53,464.86L267.42,466.18L268.72,466.9L270.37,469.82L272.46,470.17L274.01,469.43L275.02,469.91L276.7,469.67L278.85,470.98L277.04,473.82L277.88,473.88L279.28,475.37L276.75,475.24L276.38,475.66L274.08,476.19L270.88,478.1L270.67,479.4L269.96,480.38L270.24,481.89L268.54,482.7L268.54,483.89L267.8,484.4L268.97,486.93L270.53,488.65L269.94,489.86L271.8,490.02L272.86,491.53L275.33,491.6L277.63,489.94L277.44,494.24L278.72,494.57L280.3,494.08L282.73,498.66L282.12,499.62L281.99,501.64L281.93,504.08L280.83,505.52L281.34,506.59L280.69,507.56L281.9,510z'],
    ['PY', 'M299.49,526.99L300.6,523.4L300.67,521.8L302.01,519.18L306.9,518.32L309.5,518.37L312.12,519.88L312.16,520.79L312.99,522.45L312.81,526.51L315.77,527.09L316.91,526.5L318.8,527.32L319.33,528.22L319.59,530.99L319.92,532.17L320.96,532.3L322.01,531.81L323.02,532.36L323.02,534.04L322.64,535.86L322.09,537.64L321.63,540.39L319.09,542.79L316.87,543.29L313.72,542.81L310.9,541.96L313.66,537.23L313.25,535.86L310.37,534.66L306.94,532.4L304.65,531.94z'],
    ['UY', 'M313.68,551.79L315.5,551.45L318.31,553.95L319.35,553.86L322.24,555.94L324.44,557.76L326.06,560.01L324.82,561.58L325.6,563.48L324.39,565.6L321.22,567.48L319.15,566.8L317.63,567.17L315.04,565.71L313.14,565.82L311.43,563.95L311.65,561.79L312.26,561.05L312.23,557.75L312.98,554.38z'],
    ['VE', 'M275.25,430.35L275.17,431.02L273.52,431.35L274.44,432.64L274.4,434.13L273.17,435.77L274.23,438.01L275.44,437.83L276.07,435.79L275.2,434.79L275.06,432.65L278.55,431.49L278.16,430.15L279.14,429.25L280.15,431.25L282.12,431.3L283.94,432.88L284.05,433.82L286.56,433.84L289.56,433.55L291.17,434.82L293.31,435.17L294.88,434.29L294.91,433.57L298.39,433.4L301.75,433.36L299.37,434.2L300.32,435.54L302.57,435.75L304.69,437.14L305.14,439.4L306.6,439.33L307.7,440L305.48,441.65L305.23,442.68L306.19,443.72L305.5,444.24L303.77,444.69L303.83,445.99L303.07,446.76L304.96,448.88L305.34,449.67L304.31,450.74L301.17,451.78L299.16,452.22L298.35,452.88L296.12,452.18L294.04,451.82L293.52,452.08L294.77,452.8L294.66,454.67L295.05,456.43L297.42,456.67L297.58,457.25L295.57,458.05L295.25,459.23L294.09,459.68L292.01,460.33L291.47,461.19L289.29,461.37L287.74,459.89L286.89,457.12L286.14,456.14L285.12,455.53L286.54,454.14L286.45,453.51L285.65,452.68L285.09,450.83L285.31,448.82L285.93,447.88L286.44,446.38L285.45,445.89L283.85,446.21L281.83,446.06L280.7,446.36L278.72,443.95L277.09,443.59L273.49,443.86L272.82,442.88L272.13,442.65L272.03,442.06L272.36,441.02L272.14,439.89L271.52,439.27L271.16,437.97L269.72,437.79L270.49,436.13L270.84,434.12L271.65,433.06L272.74,432.25L273.45,430.83z'],
    ['', 'M307.7,440L309.54,441.03L311.28,442.86L311.35,444.31L312.41,444.38L313.91,445.74L315.02,446.72L314.57,449.24L312.87,449.97L313.02,450.62L312.5,452.07L313.75,454.09L314.64,454.1L315.01,455.67L316.72,458.09L316.04,458.19L314.49,457.96L313.58,458.7L312.31,459.19L311.43,459.31L311.12,459.85L309.74,459.71L308.01,458.41L307.81,457.12L307.09,455.71L307.54,453.33L308.32,452.35L307.67,451.05L306.71,450.63L307.08,449.4L306.42,448.76L304.96,448.88L303.07,446.76L303.83,445.99L303.77,444.69L305.5,444.24L306.19,443.72L305.23,442.68L305.48,441.65z'],
    ['', 'M315.02,446.72L318.38,447.28L318.68,446.77L320.95,446.57L323.96,447.33L322.5,449.73L322.72,451.64L323.83,453.3L323.34,454.5L323.09,455.77L322.37,456.94L320.77,456.35L319.44,456.64L318.31,456.39L318.03,457.2L318.5,457.75L318.25,458.32L316.72,458.09L315.01,455.67L314.64,454.1L313.75,454.09L312.5,452.07L313.02,450.62L312.87,449.97L314.57,449.24z'],
    ['', 'M327.89,456.41l-1.07,1.06l-1.34,0.2l-0.38,-0.78l-0.63,-0.12l-0.87,0.76l-1.22,-0.57l0.71,-1.19l0.24,-1.27l0.48,-1.2l-1.09,-1.65l-0.22,-1.91l1.46,-2.41l0.95,0.31l2.06,0.66l2.97,2.36l0.46,1.14l-1.66,2.55L327.89,456.41z'],
  ];

  const SOUTH_AMERICA_MAP = `
    <svg viewBox="240 425 145 235" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g class="meili-map__continent">
        ${SA_PATHS.map(function(p) {
          return '<path class="meili-map__land" ' + (p[0] ? 'data-cc="' + p[0] + '" ' : '') + 'd="' + p[1] + '"/>';
        }).join('')}
      </g>
      <circle class="meili-map__pulse meili-map__pulse--1" cx="272" cy="443" r="3"/>
      <circle class="meili-map__pulse meili-map__pulse--2" cx="365" cy="507" r="3"/>
      <circle class="meili-map__pulse meili-map__pulse--3" cx="295" cy="570" r="3"/>
      <path class="meili-map__route meili-map__route--1" d="M272,443 Q260,470 252,490"/>
      <path class="meili-map__route meili-map__route--2" d="M252,490 Q310,480 365,507"/>
      <path class="meili-map__route meili-map__route--3" d="M365,507 Q355,525 340,545"/>
      <path class="meili-map__route meili-map__route--4" d="M340,545 Q315,560 295,570"/>
      <path class="meili-map__route meili-map__route--5" d="M295,570 Q284,562 273,566"/>
      <g class="meili-map__cities">
        <circle class="meili-map__city" cx="272" cy="443" r="3"/>
        <circle class="meili-map__city" cx="252" cy="490" r="3"/>
        <circle class="meili-map__city" cx="365" cy="507" r="3"/>
        <circle class="meili-map__city" cx="340" cy="545" r="3"/>
        <circle class="meili-map__city" cx="295" cy="570" r="3"/>
        <circle class="meili-map__city" cx="273" cy="566" r="3"/>
      </g>
      <text class="meili-map__label" x="275" y="438">Bogotá</text>
      <text class="meili-map__label" x="242" y="502">Lima</text>
      <text class="meili-map__label" x="368" y="503">Rio</text>
      <text class="meili-map__label" x="343" y="540">São Paulo</text>
      <text class="meili-map__label" x="298" y="575">B. Aires</text>
      <text class="meili-map__label" x="257" y="562">Santiago</text>
    </svg>
  `;

  const GENERIC_ROUTE_MAP = `
    <svg viewBox="0 0 120 180" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path class="meili-map__route meili-map__route--1" d="M28,30 Q40,55 60,62"/>
      <path class="meili-map__route meili-map__route--2" d="M60,62 Q88,70 92,98"/>
      <path class="meili-map__route meili-map__route--3" d="M92,98 Q78,120 60,128"/>
      <path class="meili-map__route meili-map__route--4" d="M60,128 Q40,138 30,150"/>
      <circle class="meili-map__pulse meili-map__pulse--1" cx="28" cy="30" r="3"/>
      <circle class="meili-map__pulse meili-map__pulse--2" cx="92" cy="98" r="3"/>
      <circle class="meili-map__pulse meili-map__pulse--3" cx="30" cy="150" r="3"/>
      <g class="meili-map__cities">
        <circle class="meili-map__city" cx="28" cy="30" r="3"/>
        <circle class="meili-map__city" cx="60" cy="62" r="3"/>
        <circle class="meili-map__city" cx="92" cy="98" r="3"/>
        <circle class="meili-map__city" cx="60" cy="128" r="3"/>
        <circle class="meili-map__city" cx="30" cy="150" r="3"/>
      </g>
    </svg>
  `;

  const LOADING_MAP = REGION_MAP === 'south-america' ? SOUTH_AMERICA_MAP : GENERIC_ROUTE_MAP;

  // ==========================================================================
  // Countries data / form (largely carried over from v1)
  // ==========================================================================
  function init() {
    fetchCountries().then(function() {
      // Restore a shared/bookmarked itinerary if the URL carries ?trip=
      const params = new URLSearchParams(window.location.search);
      const tripId = params.get('trip');
      if (tripId && /^[a-z0-9]{6,20}$/.test(tripId)) {
        restoreTrip(tripId);
      }
    });
    setupEventListeners();
    setupDelegation();
  }

  async function fetchCountries() {
    try {
      const response = await fetch(COUNTRIES_ENDPOINT);
      if (!response.ok) throw new Error('Failed to fetch countries');
      const data = await response.json();
      countriesData = data.countries || data;
      buildCityIndex();
      renderCountriesDropdown();
      updatePlaceholder();
    } catch (error) {
      console.error('Error fetching countries:', error);
      if (countriesSelected) {
        countriesSelected.innerHTML = '<span class="meili-form__multiselect-placeholder">Failed to load countries</span>';
      }
    }
  }

  function buildCityIndex() {
    cityToCountry = {};
    countryCodeByName = {};
    countriesData.forEach(function(c) {
      if (typeof c !== 'object') return;
      countryCodeByName[c.name] = c.code;
      (c.cities || []).forEach(function(city) {
        cityToCountry[city.toLowerCase()] = c.name;
      });
    });
  }

  function countryForLocation(location) {
    const loc = (location || '').toLowerCase();
    if (cityToCountry[loc]) return cityToCountry[loc];
    // "Cusco, Peru" style locations
    for (let i = 0; i < selectedCountries.length; i++) {
      if (loc.indexOf(selectedCountries[i].toLowerCase()) > -1) return selectedCountries[i];
    }
    const req = trip.request && trip.request.countries;
    if (req && req.length === 1) return req[0];
    if (selectedCountries.length === 1) return selectedCountries[0];
    return null;
  }

  function renderCountriesDropdown(filter) {
    filter = filter || '';
    const filtered = countriesData.filter(function(country) {
      const name = typeof country === 'string' ? country : country.name;
      return name.toLowerCase().indexOf(filter.toLowerCase()) > -1;
    });

    countriesOptions.innerHTML = filtered.map(function(country) {
      const name = typeof country === 'string' ? country : country.name;
      const code = typeof country === 'object' ? country.code : '';
      const isSelected = selectedCountries.indexOf(name) > -1;
      return '<div class="meili-form__multiselect-option ' + (isSelected ? 'selected' : '') + '" data-value="' + escapeAttr(name) + '">' +
        '<div class="meili-form__multiselect-option-check"></div>' +
        '<span>' + (code ? flagFor(code) + ' ' : '') + escapeHtml(name) + '</span>' +
        '</div>';
    }).join('');

    countriesOptions.querySelectorAll('.meili-form__multiselect-option').forEach(function(option) {
      option.addEventListener('click', function() { toggleCountry(option.dataset.value); });
    });
  }

  function toggleCountry(countryName) {
    const index = selectedCountries.indexOf(countryName);
    if (index > -1) selectedCountries.splice(index, 1);
    else selectedCountries.push(countryName);

    updateSelectedDisplay();
    updateStartingCountryOptions();
    renderCountriesDropdown(countriesSearch.value);
    countriesInput.value = selectedCountries.join(',');
  }

  function updateSelectedDisplay() {
    if (selectedCountries.length === 0) { updatePlaceholder(); return; }
    countriesSelected.innerHTML = selectedCountries.map(function(country) {
      return '<span class="meili-form__multiselect-tag">' +
        flagFor(countryCodeByName[country]) + ' ' + escapeHtml(country) +
        '<button type="button" class="meili-form__multiselect-tag-remove" data-country="' + escapeAttr(country) + '" aria-label="Remove ' + escapeAttr(country) + '">&times;</button>' +
        '</span>';
    }).join('');

    countriesSelected.querySelectorAll('.meili-form__multiselect-tag-remove').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        toggleCountry(btn.dataset.country);
      });
    });
  }

  function updatePlaceholder() {
    countriesSelected.innerHTML = '<span class="meili-form__multiselect-placeholder">' +
      (LOCALE === 'es' ? 'Haz clic para elegir países…' : 'Click to select countries…') + '</span>';
  }

  function updateStartingCountryOptions() {
    const currentValue = startingCountrySelect.value;
    startingCountrySelect.innerHTML = '<option value="">' + (LOCALE === 'es' ? 'Aún no estoy seguro' : 'Not sure yet') + '</option>';
    selectedCountries.forEach(function(country) {
      const option = document.createElement('option');
      option.value = country;
      option.textContent = country;
      startingCountrySelect.appendChild(option);
    });
    if (selectedCountries.indexOf(currentValue) > -1) {
      startingCountrySelect.value = currentValue;
    } else if (selectedCountries.length > 0) {
      startingCountrySelect.value = selectedCountries[0];
      updateStartingCityOptions(selectedCountries[0]);
    } else {
      startingCountrySelect.value = '';
      updateStartingCityOptions('');
    }
  }

  function updateStartingCityOptions(countryName) {
    startingCitySelect.innerHTML = '';
    if (!countryName) {
      startingCitySelect.innerHTML = '<option value="">' + (LOCALE === 'es' ? 'Selecciona un país primero' : 'Select a country first') + '</option>';
      startingCitySelect.disabled = true;
      return;
    }
    startingCitySelect.disabled = false;
    startingCitySelect.innerHTML = '<option value="">' + (LOCALE === 'es' ? 'Aún no estoy seguro' : 'Not sure yet') + '</option>';
    const countryData = countriesData.find(function(c) {
      return (typeof c === 'string' ? c : c.name) === countryName;
    });
    if (countryData && typeof countryData === 'object' && countryData.cities) {
      countryData.cities.forEach(function(city) {
        const option = document.createElement('option');
        option.value = city;
        option.textContent = city;
        startingCitySelect.appendChild(option);
      });
    }
  }

  function setupEventListeners() {
    if (!form) return;

    countriesSelected.addEventListener('click', function() {
      countriesDropdown.classList.toggle('hidden');
      countriesSelected.classList.toggle('active');
      if (!countriesDropdown.classList.contains('hidden')) countriesSearch.focus();
    });

    countriesSearch.addEventListener('input', function(e) {
      renderCountriesDropdown(e.target.value);
    });

    document.addEventListener('click', function(e) {
      // Selecting an option re-renders the dropdown, detaching the clicked
      // node before this bubbled handler runs — a detached target is not
      // "outside", so don't close for it.
      if (!document.documentElement.contains(e.target)) return;
      if (!countriesContainer.contains(e.target)) {
        countriesDropdown.classList.add('hidden');
        countriesSelected.classList.remove('active');
      }
    });

    startingCountrySelect.addEventListener('change', function(e) {
      updateStartingCityOptions(e.target.value);
    });

    priorityCheckboxes.forEach(function(checkbox) {
      checkbox.addEventListener('change', function() {
        const checkedCount = Array.prototype.filter.call(priorityCheckboxes, function(cb) { return cb.checked; }).length;
        prioritiesCounter.textContent = checkedCount + '/2 ' + (LOCALE === 'es' ? 'seleccionadas' : 'selected');
        priorityCheckboxes.forEach(function(cb) {
          cb.disabled = checkedCount >= 2 && !cb.checked;
        });
      });
    });

    form.addEventListener('submit', handleSubmit);

    if (retryBtn) {
      retryBtn.addEventListener('click', function() {
        errorSection.classList.add('hidden');
        form.classList.remove('hidden');
        if (plannerHeader) plannerHeader.classList.remove('hidden');
      });
    }

    // Close modals on overlay click / escape
    document.addEventListener('click', function(e) {
      if (e.target.classList && e.target.classList.contains('meili-modal-overlay')) {
        e.target.classList.remove('active');
      }
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        document.querySelectorAll('.meili-modal-overlay.active').forEach(function(m) { m.classList.remove('active'); });
        closeEsimSheet();
      }
    });
  }

  // ==========================================================================
  // Form validation + submit
  // ==========================================================================
  function validateForm() {
    let isValid = true;
    function gate(id, ok) {
      const elx = document.getElementById(id);
      if (elx) elx.classList.toggle('hidden', ok);
      if (!ok) isValid = false;
    }
    gate('countries-error', selectedCountries.length > 0);
    const totalDaysInput = document.getElementById('total_days');
    gate('total-days-error', !!(totalDaysInput.value && parseInt(totalDaysInput.value, 10) >= 1));
    gate('travel-pace-error', !!form.querySelector('input[name="travel_pace"]:checked'));
    gate('priorities-error', Array.prototype.some.call(priorityCheckboxes, function(cb) { return cb.checked; }));
    gate('transport-error', !!form.querySelector('input[name="transport_preference"]:checked'));
    gate('optimisation-error', !!form.querySelector('input[name="optimisation_goal"]:checked'));
    return isValid;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateForm()) return;

    const payload = {
      countries: selectedCountries.slice(),
      total_days: parseInt(document.getElementById('total_days').value, 10),
      travel_pace: form.querySelector('input[name="travel_pace"]:checked').value,
      priorities: Array.prototype.filter.call(priorityCheckboxes, function(cb) { return cb.checked; }).map(function(cb) { return cb.value; }),
      transport_preference: form.querySelector('input[name="transport_preference"]:checked').value,
      optimisation_goal: form.querySelector('input[name="optimisation_goal"]:checked').value,
      locale: LOCALE,
      stream_version: 2,
    };
    const startingCountry = startingCountrySelect.value;
    if (startingCountry) payload.starting_country = startingCountry;
    const startingCity = startingCitySelect.value;
    if (startingCity) payload.starting_city = startingCity;

    meiliTrack('plan_requested', {
      countries: payload.countries.join(','),
      country_count: payload.countries.length,
      total_days: payload.total_days,
      travel_pace: payload.travel_pace,
    });

    await runStream(PLAN_ENDPOINT, payload, { kind: 'plan' });
  }

  // ==========================================================================
  // Streaming pipeline (plan + refine share it)
  // ==========================================================================
  async function runStream(endpoint, payload, opts) {
    isSubmitting = true;
    setLoadingState(true);
    trip.request = payload.countries ? payload : trip.request;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        throw Object.assign(new Error(STR.rateLimited), { friendly: true });
      }
      if (!response.ok) {
        throw new Error('API error: ' + response.status);
      }

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.indexOf('text/event-stream') === -1) {
        throw new Error('Unexpected response');
      }

      await consumeStream(response, opts);
    } catch (error) {
      console.error('Meili stream error:', error);
      meiliTrack('error', { stage: opts.kind === 'refine' ? 'refine' : 'plan_generation', message: String(error && error.message || error).slice(0, 100) });
      if (streamPhase === 'streaming' && trip.segments.length > 0) {
        // Keep what we have; mark partial
        await finishStreaming(null, true);
      } else if (opts.kind === 'refine') {
        hideRevisingOverlay();
        showToast(error.friendly ? error.message : STR.genError);
      } else {
        showError(error.friendly ? error.message : STR.genError);
      }
    } finally {
      isSubmitting = false;
      setLoadingState(false);
    }
  }

  async function consumeStream(response, opts) {
    if (opts.kind === 'plan') {
      resetTripState();
      enterLoading();
    }
    // For refine: keep the itinerary visible under the revising overlay until
    // the new header arrives, then rebuild the shell in place.

    streamPhase = 'loading';

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let eventType = 'message';
    let sawContent = false;
    let completeData = null;

    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.indexOf('event: ') === 0) { eventType = line.slice(7).trim(); continue; }
        if (line.indexOf('data: ') !== 0) continue;
        const data = line.slice(6);
        if (data === '[DONE]') continue;

        let parsed;
        try { parsed = JSON.parse(data); } catch (err) { continue; }

        if (eventType === 'progress') {
          onProgressEvent(parsed);
        } else if (eventType === 'content') {
          if (!sawContent) { sawContent = true; }
          onContentEvent(parsed, opts);
        } else if (eventType === 'complete') {
          completeData = parsed;
        } else if (eventType === 'error') {
          const msg = parsed && parsed.error && parsed.error.message;
          throw new Error(msg || 'stream error');
        }
      }
    }

    if (!sawContent) throw new Error('empty stream');
    await finishStreaming(completeData, false);
  }

  function resetTripState() {
    trip.id = null; trip.title = ''; trip.totalDays = 0; trip.style = '';
    trip.focus = []; trip.route = []; trip.segments = []; trip.notes = [];
    trip.esim = null; trip.createdAt = null; trip.partial = false;
    segmentCountrySeen = {};
    const bar = document.getElementById('meili-esim-bar');
    if (bar) bar.remove();
    const sheet = document.getElementById('meili-esim-sheet');
    if (sheet) sheet.remove();
  }

  // ==========================================================================
  // Loading experience — the SVG map narrates while Meili thinks
  // ==========================================================================
  let currentStage = -1;

  function enterLoading() {
    streamPhase = 'loading';
    currentStage = -1;
    form.classList.add('hidden');
    if (plannerHeader) plannerHeader.classList.add('hidden');
    errorSection.classList.add('hidden');
    resultsSection.classList.remove('hidden');

    resultsContent.innerHTML = '' +
      '<div class="meili-loading" id="meili-loading">' +
        '<div class="meili-loading__visual"><div class="meili-loading__map">' + LOADING_MAP + '</div></div>' +
        '<div class="meili-loading__content">' +
          '<div class="meili-loading__icon">' + ICONS.sparkle + '</div>' +
          '<h3 class="meili-loading__text" id="loading-text"></h3>' +
          '<p class="meili-loading__subtext" id="loading-subtext"></p>' +
        '</div>' +
        '<div class="meili-loading__progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" id="loading-progressbar">' +
          '<div class="meili-loading__progress-bar" id="loading-bar"></div>' +
        '</div>' +
        '<div class="meili-loading__states" id="loading-states">' +
          STR.stageLabels.map(function(label, i) {
            return '<div class="meili-loading__state" data-stage="' + i + '"><div class="meili-loading__state-dot"></div><span>' + escapeHtml(label) + '</span></div>';
          }).join('') +
        '</div>' +
        '<p class="meili-loading__disclaimer">' + escapeHtml(STR.disclaimerLoading) + '</p>' +
      '</div>';

    // Light up the traveller's countries on the map, and redraw the route
    // FOR THEIR TRIP: when selected countries exist on the map, the demo
    // route/labels are replaced by pulses at each selected country and an
    // animated leg connecting them in selection order.
    const codes = selectedCountries.map(function(c) { return countryCodeByName[c]; }).filter(Boolean);
    const svg = resultsContent.querySelector('.meili-loading__map svg');
    const matchedPaths = svg
      ? codes.map(function(code) { return svg.querySelector('.meili-map__land[data-cc="' + code + '"]'); }).filter(Boolean)
      : [];

    if (svg && matchedPaths.length > 0) {
      // Drop the generic showcase route — it isn't this traveller's trip.
      svg.querySelectorAll('.meili-map__route, .meili-map__pulse, .meili-map__city, .meili-map__label').forEach(function(n) { n.remove(); });

      const SVGNS = 'http://www.w3.org/2000/svg';
      const pts = matchedPaths.map(function(p) {
        const b = p.getBBox();
        return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
      });

      if (pts.length > 1) {
        const d = pts.map(function(p, i) { return (i === 0 ? 'M' : 'L') + p.x.toFixed(1) + ',' + p.y.toFixed(1); }).join(' ');
        const route = document.createElementNS(SVGNS, 'path');
        route.setAttribute('d', d);
        route.setAttribute('pathLength', '100');
        route.setAttribute('class', 'meili-map__dynroute');
        svg.appendChild(route);
      }

      pts.forEach(function(pt, i) {
        const dot = document.createElementNS(SVGNS, 'circle');
        dot.setAttribute('cx', pt.x); dot.setAttribute('cy', pt.y); dot.setAttribute('r', '3');
        dot.setAttribute('class', 'meili-map__city');
        svg.appendChild(dot);
        const pulse = document.createElementNS(SVGNS, 'circle');
        pulse.setAttribute('cx', pt.x); pulse.setAttribute('cy', pt.y); pulse.setAttribute('r', '3');
        pulse.setAttribute('class', 'meili-map__pulse');
        pulse.style.animationDelay = (i * 0.8) + 's';
        svg.appendChild(pulse);
      });
    }

    matchedPaths.forEach(function(path, i) {
      setTimeout(function() { path.classList.add('is-selected'); }, REDUCED_MOTION ? 0 : 350 * (i + 1));
    });

    setStage(0, 5);

    setTimeout(function() {
      const loadingEl = document.getElementById('meili-loading');
      if (loadingEl) loadingEl.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'center' });
    }, 100);
  }

  function stageForPercent(percent) {
    if (percent < 15) return 0;
    if (percent < 70) return 1;
    if (percent < 90) return 2;
    return 3;
  }

  function setStage(stage, percent) {
    const bar = document.getElementById('loading-bar');
    const pb = document.getElementById('loading-progressbar');
    if (bar && typeof percent === 'number') {
      bar.style.width = Math.max(3, percent) + '%';
      if (pb) pb.setAttribute('aria-valuenow', String(percent));
    }
    if (stage === currentStage) return;
    currentStage = stage;

    const textEl = document.getElementById('loading-text');
    const subEl = document.getElementById('loading-subtext');
    const stageData = STR.stages[stage] || STR.stages[0];
    if (textEl) {
      textEl.classList.add('fade-out');
      setTimeout(function() {
        textEl.textContent = contextualStageTitle(stage, stageData.t);
        if (subEl) subEl.textContent = stageData.s;
        textEl.classList.remove('fade-out');
      }, REDUCED_MOTION ? 0 : 150);
    }

    document.querySelectorAll('#loading-states .meili-loading__state').forEach(function(s, i) {
      s.classList.remove('complete', 'active');
      if (i < stage) s.classList.add('complete');
      else if (i === stage) s.classList.add('active');
    });
  }

  function contextualStageTitle(stage, fallback) {
    const c = (trip.request && trip.request.countries) || selectedCountries;
    if (!c || c.length === 0) return fallback;
    if (stage === 1) {
      return LOCALE === 'es'
        ? (c.length > 1 ? 'Trazando ' + c[0] + ' → ' + c[c.length - 1] + '…' : 'Trazando tu ruta por ' + c[0] + '…')
        : (c.length > 1 ? 'Mapping ' + c[0] + ' → ' + c[c.length - 1] + '…' : 'Mapping your route through ' + c[0] + '…');
    }
    return fallback;
  }

  function onProgressEvent(parsed) {
    if (streamPhase !== 'loading') return; // once streaming, content drives UI
    const percent = typeof parsed.percent === 'number' ? parsed.percent :
      ({ thinking: 10, generating: 45, citing: 90, complete: 100 })[parsed.state] || 10;
    setStage(stageForPercent(percent), percent);
  }

  // ==========================================================================
  // Itinerary shell + progressive rendering
  // ==========================================================================
  function onContentEvent(parsed, opts) {
    const type = parsed.type;
    const content = parsed.content || parsed;

    switch (type) {
      case 'header':
        trip.title = content.title || 'Your Trip';
        trip.totalDays = content.days || 0;
        trip.style = content.style || '';
        trip.focus = content.focus || [];
        enterShell(opts);
        break;

      case 'route_overview':
        if (Array.isArray(content.route_sequence)) {
          trip.route = content.route_sequence.map(function(stop) {
            return typeof stop === 'string'
              ? { city: stop, nights: 0 }
              : { city: stop.location || stop.city || String(stop), nights: stop.nights || 0 };
          });
        }
        // May run before the shell DOM exists (v1 workers burst all events
        // at once while the shell swap is inside a view transition) — the
        // shell build mounts collected state, so a missing rail is fine.
        renderRouteRail();
        break;

      case 'day_segment': {
        const seg = normalizeSegment(content);
        trip.segments.push(seg);
        // route nights fill-in (route_overview rarely has nights)
        const routeStop = trip.route.find(function(r) { return r.city.toLowerCase() === seg.city.toLowerCase() && !r.nights; });
        if (routeStop) { routeStop.nights = seg.nights; renderRouteRail(); }
        if (document.getElementById('meili-timeline')) {
          mountSegmentCard(seg);
        }
        break;
      }

      case 'meili_note': {
        const noteText = content.note || content.text || '';
        if (noteText && noteText.trim().length >= 10) {
          trip.notes.push({ context: content.context || '', text: noteText, type: content.type || 'general' });
        }
        break;
      }

      case 'esim_recommendations':
        trip.esim = {
          recommendations: content.recommendations || [],
          justification: content.justification || '',
          regional: content.regional_option || null,
        };
        break;

      case 'esim_recommendation':
        // Legacy single-recommendation fallback (no product match)
        trip.esim = { recommendations: [], justification: content.justification || '', regional: null, ctaUrl: content.cta_url || '/collections/esim' };
        break;
    }
  }

  function normalizeSegment(content) {
    let rawDays = content.day_range || content.days || '';
    rawDays = String(rawDays).replace(/^Days?\s*/i, '').trim();
    let nights = content.nights || 0;
    if (!nights && rawDays.indexOf('-') > -1) {
      const parts = rawDays.split('-').map(function(n) { return parseInt(n.trim(), 10); });
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) nights = parts[1] - parts[0] + 1;
    }
    const seg = {
      index: trip.segments.length,
      city: content.location || 'Unknown',
      daysLabel: rawDays ? (rawDays.indexOf('-') > -1 ? (LOCALE === 'es' ? 'Días ' : 'Days ') + rawDays : STR.day + ' ' + rawDays) : '',
      nights: nights,
      subtitle: content.subtitle || content.purpose || '',
      highlights: content.highlights || [],
      days: [],
      guidance: content.pacing_rationale || '',
      practicalNotes: content.practical_notes || [],
      transport: content.transport_to_next || null,
      country: countryForLocation(content.location),
    };
    if (Array.isArray(content.day_by_day)) {
      seg.days = content.day_by_day.map(function(d) {
        return {
          day: d.day, title: d.title || (STR.day + ' ' + d.day),
          activities: d.activities || [], purpose: d.purpose || '',
          note: d.meili_note || '', icon: d.icon || 'explore',
        };
      });
    } else if (content.activities) {
      const acts = Array.isArray(content.activities)
        ? content.activities
        : [].concat(content.activities.day || [], content.activities.night || []);
      if (acts.length) {
        seg.days = [{ day: parseInt(rawDays, 10) || seg.index + 1, title: '', activities: acts, purpose: '', note: '', icon: 'explore' }];
      }
    }
    if (seg.highlights.length === 0 && seg.days.length > 0) {
      seg.highlights = seg.days[0].activities.slice(0, 3);
    }
    return seg;
  }

  // Resolved once the current shell's DOM actually exists. When a v1 worker
  // bursts all events + closes in a single network chunk, finishStreaming
  // would otherwise run before startViewTransition's async callback has
  // built the shell — and every completion-time insertion would miss.
  let shellReadyResolve = null;
  let shellReady = Promise.resolve();

  function enterShell(opts) {
    streamPhase = 'streaming';
    hideRevisingOverlay();
    shellReady = new Promise(function(resolve) { shellReadyResolve = resolve; });

    const build = function() {
      resultsContent.innerHTML = renderShellHtml();
      // Mount anything that streamed in before the shell existed (v1 workers
      // burst all content in one tick; the transition callback runs later).
      renderRouteRail();
      trip.segments.forEach(function(seg) { mountSegmentCard(seg); });
      announce(trip.title);
      const shell = document.getElementById('meili-output');
      if (shell) shell.scrollIntoView({ behavior: 'auto', block: 'start' });
      if (shellReadyResolve) { shellReadyResolve(); shellReadyResolve = null; }
    };

    if (!REDUCED_MOTION && document.startViewTransition) {
      document.startViewTransition(build);
    } else {
      build();
    }
  }

  function renderShellHtml() {
    const daysLabel = trip.totalDays ? trip.totalDays + ' ' + STR.days : '';
    const metaBits = [daysLabel, trip.style].filter(Boolean).join(' · ');
    return '' +
      '<div class="meili-output" id="meili-output">' +
        '<header class="meili-hero">' +
          '<div class="meili-hero__topbar">' +
            '<button type="button" class="meili-hero__back" data-meili-action="start-over">' + ICONS.arrowLeft + '<span>' + escapeHtml(STR.planAnother) + '</span></button>' +
            '<div class="meili-hero__actions" id="hero-actions"></div>' +
          '</div>' +
          '<div class="meili-hero__body">' +
            '<div class="meili-hero__brand"><span class="meili-hero__brand-name">Meili</span><span class="meili-hero__brand-dot" aria-hidden="true"></span><span class="meili-hero__brand-status" id="hero-status">' + escapeHtml(STR.writingPlan) + '</span></div>' +
            '<h1 class="meili-hero__title">' + escapeHtml(trip.title) + '</h1>' +
            '<p class="meili-hero__meta">' + escapeHtml(metaBits) + '</p>' +
            (trip.focus && trip.focus.length ? '<div class="meili-hero__focus">' + trip.focus.map(function(f) { return '<span class="meili-hero__focus-chip">' + escapeHtml(f) + '</span>'; }).join('') + '</div>' : '') +
          '</div>' +
        '</header>' +
        '<p class="meili-output__disclaimer">' + escapeHtml(STR.disclaimerOutput) + '</p>' +
        '<nav class="meili-rail" id="meili-rail" aria-label="Route">' + renderRailSkeleton() + '</nav>' +
        '<div class="meili-timeline" id="meili-timeline" aria-live="polite" aria-busy="true">' +
          renderSkeletonCard() +
        '</div>' +
        '<div id="meili-after-timeline"></div>' +
      '</div>';
  }

  function renderRailSkeleton() {
    return '<div class="meili-rail__skeleton"><span class="meili-skel meili-skel--chip"></span><span class="meili-skel meili-skel--chip"></span><span class="meili-skel meili-skel--chip"></span></div>';
  }

  function renderSkeletonCard() {
    return '<div class="meili-skeleton-card" data-skeleton aria-hidden="true">' +
      '<div class="meili-skeleton-card__head"><span class="meili-skel meili-skel--badge"></span><div class="meili-skeleton-card__lines"><span class="meili-skel meili-skel--line" style="width:52%"></span><span class="meili-skel meili-skel--line" style="width:34%"></span></div></div>' +
      '<div class="meili-skeleton-card__chips"><span class="meili-skel meili-skel--chip"></span><span class="meili-skel meili-skel--chip"></span></div>' +
      '</div>';
  }

  function renderRouteRail() {
    const rail = document.getElementById('meili-rail');
    if (!rail || trip.route.length === 0) return;
    rail.innerHTML = trip.route.map(function(stop, i) {
      return '<button type="button" class="meili-rail__chip' + (i === 0 ? ' active' : '') + '" data-meili-action="rail-jump" data-index="' + i + '" data-city="' + escapeAttr(stop.city) + '">' +
        '<span class="meili-rail__num">' + (i + 1) + '</span>' +
        '<span class="meili-rail__city">' + escapeHtml(stop.city) + '</span>' +
        (stop.nights ? '<span class="meili-rail__nights">' + stop.nights + ' ' + pluralNights(stop.nights) + '</span>' : '') +
        '</button>' + (i < trip.route.length - 1 ? '<span class="meili-rail__connector" aria-hidden="true"></span>' : '');
    }).join('');
  }

  function mountSegmentCard(seg) {
    const timeline = document.getElementById('meili-timeline');
    if (!timeline) return;
    if (timeline.querySelector('[data-segment="' + seg.index + '"]')) return; // already mounted

    // Transport connector BEFORE this card (from the previous segment)
    const prev = trip.segments[seg.index - 1];
    if (prev && prev.transport && prev.transport.destination) {
      const conn = el(renderTransportConnector(prev.transport));
      timeline.insertBefore(conn, timeline.querySelector('[data-skeleton]'));
    }

    const card = el(renderSegmentCard(seg));
    const skeleton = timeline.querySelector('[data-skeleton]');
    if (skeleton) timeline.insertBefore(card, skeleton);
    else timeline.appendChild(card);

    if (!REDUCED_MOTION) {
      card.classList.add('meili-enter');
      requestAnimationFrame(function() {
        requestAnimationFrame(function() { card.classList.add('meili-enter-active'); });
      });
    }

    announce(seg.city + (seg.daysLabel ? ' — ' + seg.daysLabel : ''));
    syncRail();
  }

  function renderSegmentCard(seg) {
    const expanded = seg.index === 0;
    return '' +
    '<article class="meili-card' + (expanded ? ' expanded' : '') + '" data-segment="' + seg.index + '" data-city="' + escapeAttr(seg.city) + '">' +
      '<button type="button" class="meili-card__head" data-meili-action="toggle-segment" data-index="' + seg.index + '" aria-expanded="' + expanded + '">' +
        '<span class="meili-card__nights"><strong>' + (seg.nights || '–') + '</strong><small>' + pluralNights(seg.nights) + '</small></span>' +
        '<span class="meili-card__headline">' +
          '<span class="meili-card__city">' + escapeHtml(seg.city) + (seg.country && countryCodeByName[seg.country] ? ' <span class="meili-card__flag">' + flagFor(countryCodeByName[seg.country]) + '</span>' : '') + '</span>' +
          '<span class="meili-card__sub">' + escapeHtml(seg.subtitle) + '</span>' +
        '</span>' +
        '<span class="meili-card__days-chip">' + escapeHtml(seg.daysLabel) + '</span>' +
        '<span class="meili-card__chevron" aria-hidden="true">' + ICONS.chevronDown + '</span>' +
      '</button>' +
      '<div class="meili-card__body">' +
        '<div class="meili-card__inner">' +
          (seg.highlights.length ? '<div class="meili-card__highlights">' + seg.highlights.map(function(h) { return '<span class="meili-tag">' + escapeHtml(h) + '</span>'; }).join('') + '</div>' : '') +
          (seg.days.length ? '<div class="meili-dayplan"><h4 class="meili-dayplan__title">' + escapeHtml(STR.dayByDay) + '</h4>' +
            seg.days.map(function(d) { return renderDayRow(d); }).join('') + '</div>' : '') +
          (seg.guidance ? '<div class="meili-guidance">' + ICONS.star + '<div><span class="meili-guidance__label">Meili</span><p>' + escapeHtml(seg.guidance) + '</p></div></div>' : '') +
          (seg.practicalNotes.length ? '<ul class="meili-practical">' + seg.practicalNotes.map(function(n) { return '<li>' + escapeHtml(n) + '</li>'; }).join('') + '</ul>' : '') +
        '</div>' +
      '</div>' +
    '</article>';
  }

  function renderDayRow(d) {
    return '<div class="meili-day">' +
      '<div class="meili-day__rail" aria-hidden="true"><span class="meili-day__icon">' + dayIcon(d.icon) + '</span></div>' +
      '<div class="meili-day__content">' +
        '<div class="meili-day__head"><span class="meili-day__num">' + STR.day + ' ' + escapeHtml(d.day) + '</span>' + (d.title ? '<span class="meili-day__title">' + escapeHtml(d.title) + '</span>' : '') + '</div>' +
        (d.activities.length ? '<ul class="meili-day__acts">' + d.activities.map(function(a) { return '<li>' + escapeHtml(a) + '</li>'; }).join('') + '</ul>' : '') +
        (d.note ? '<p class="meili-day__note">' + ICONS.star + ' ' + escapeHtml(d.note) + '</p>' : '') +
      '</div>' +
    '</div>';
  }

  function renderTransportConnector(t) {
    const detailBits = [t.duration, t.distance].filter(Boolean).join(' · ');
    return '<div class="meili-leg" role="note">' +
      '<span class="meili-leg__line" aria-hidden="true"></span>' +
      '<span class="meili-leg__icon">' + transportIcon(t.mode) + '</span>' +
      '<span class="meili-leg__text"><strong>' + escapeHtml(t.mode || '') + '</strong>' +
        (detailBits ? ' · ' + escapeHtml(detailBits) : '') +
        (t.destination ? ' → ' + escapeHtml(t.destination) : '') + '</span>' +
      (t.tip ? '<span class="meili-leg__tip">' + escapeHtml(t.tip) + '</span>' : '') +
    '</div>';
  }

  // ==========================================================================
  // Completion: esim cards, notes, refine, share, permalink
  // ==========================================================================
  async function finishStreaming(completeData, partial) {
    await shellReady; // shell DOM must exist before completion-time inserts
    streamPhase = 'done';
    trip.partial = !!partial;
    trip.id = (completeData && completeData.itinerary_id) || trip.id;

    const timeline = document.getElementById('meili-timeline');
    if (timeline) {
      timeline.setAttribute('aria-busy', 'false');
      const skeleton = timeline.querySelector('[data-skeleton]');
      if (skeleton) skeleton.remove();
    }

    // If route_overview never arrived, build the rail from the segments.
    if (trip.route.length === 0 && trip.segments.length > 0) {
      trip.route = trip.segments.map(function(s) { return { city: s.city, nights: s.nights }; });
      renderRouteRail();
    }
    // On completion the status clears (the meta line already carries the day
    // count — no duplication); the CSS hides the orphaned brand dot. Partial
    // streams keep the interruption notice; restores overwrite with the date.
    const status = document.getElementById('hero-status');
    if (status) {
      status.textContent = trip.partial ? STR.interrupted : '';
      if (!trip.partial) status.classList.add('done');
    }

    insertConnectivityCards();
    renderAfterTimeline();
    renderHeroActions();
    renderStickyBar();

    if (trip.id) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('trip', trip.id);
        history.pushState({ trip: trip.id }, '', url.toString());
      } catch (e) { /* ignore */ }
    }

    meiliTrack('plan_generated', {
      section_count: trip.segments.length,
      has_esim: !!(trip.esim && trip.esim.recommendations.length),
      itinerary_id: trip.id || '',
      partial: trip.partial,
    });
  }

  function esimForCountry(country) {
    if (!trip.esim) return null;
    return trip.esim.recommendations.find(function(r) { return r.country === country; }) || null;
  }

  function insertConnectivityCards() {
    if (!trip.esim || !trip.esim.recommendations.length) return;
    const timeline = document.getElementById('meili-timeline');
    if (!timeline) return;

    // Regional "one eSIM for the whole route" card leads when available.
    if (trip.esim.regional && trip.segments.length > 1) {
      const regional = el(renderRegionalCard(trip.esim.regional));
      timeline.parentNode.insertBefore(regional, timeline);
    }

    // Per-country card after that country's FIRST city card.
    const placed = {};
    trip.segments.forEach(function(seg) {
      if (!seg.country || placed[seg.country]) return;
      const rec = esimForCountry(seg.country);
      if (!rec) return;
      placed[seg.country] = true;
      const cardEl = timeline.querySelector('[data-segment="' + seg.index + '"]');
      if (!cardEl) return;
      const connectivity = el(renderConnectivityCard(rec));
      cardEl.insertAdjacentElement('afterend', connectivity);
    });
  }

  function renderEsimThumb(rec, size) {
    if (rec.image_url) {
      return '<img class="meili-esim__thumb" src="' + escapeAttr(rec.image_url) + '" alt="' + escapeAttr(rec.product_title || '') + '" width="' + size + '" height="' + size + '" loading="lazy">';
    }
    return '<span class="meili-esim__thumb meili-esim__thumb--flag" aria-hidden="true">' + flagFor(rec.country_code || (rec.region_code || '')) + '</span>';
  }

  function renderConnectivityCard(rec) {
    const daysBit = rec.trip_days ? STR.daysThere.replace(':n', rec.trip_days) + ' · ' : '';
    const alts = rec.alternatives || [];
    return '' +
    '<aside class="meili-esim" data-esim-country="' + escapeAttr(rec.country) + '">' +
      '<div class="meili-esim__badge">' + ICONS.wifi + '</div>' +
      renderEsimThumb(rec, 56) +
      '<div class="meili-esim__info">' +
        '<h4 class="meili-esim__title">' + escapeHtml(STR.stayConnected) + ' ' + escapeHtml(rec.country) + '</h4>' +
        '<p class="meili-esim__meta">' + escapeHtml(daysBit) + '<strong>' + escapeHtml(rec.data_amount) + ' / ' + rec.validity_days + ' ' + STR.days + '</strong> ' + escapeHtml(STR.recommended) + '</p>' +
        (alts.length ? '<div class="meili-esim__alts" role="group">' +
          '<button type="button" class="meili-esim__alt selected" data-meili-action="esim-alt" data-variant="' + escapeAttr(rec.variant_id) + '" data-price="' + escapeAttr(rec.price) + '">' + escapeHtml(rec.data_amount) + '</button>' +
          alts.map(function(a) {
            return '<button type="button" class="meili-esim__alt" data-meili-action="esim-alt" data-variant="' + escapeAttr(a.variant_id) + '" data-price="' + escapeAttr(a.price) + '">' + escapeHtml(a.data_amount) + '</button>';
          }).join('') + '</div>' : '') +
        '<p class="meili-esim__small">' + escapeHtml(STR.instantQr) + '</p>' +
      '</div>' +
      '<div class="meili-esim__buy">' +
        '<span class="meili-esim__price" data-esim-price>' + escapeHtml(rec.price) + '</span>' +
        '<button type="button" class="meili-esim__cta" data-meili-action="add-to-cart" data-variant-id="' + escapeAttr(rec.variant_id) + '">' + ICONS.cart + '<span>' + escapeHtml(STR.addToCart) + '</span></button>' +
      '</div>' +
    '</aside>';
  }

  function renderRegionalCard(reg) {
    const covers = STR.coversCountries.replace(':n', (reg.covers || []).length);
    return '' +
    '<aside class="meili-esim meili-esim--regional">' +
      '<div class="meili-esim__badge">' + ICONS.wifi + '</div>' +
      renderEsimThumb(reg, 56) +
      '<div class="meili-esim__info">' +
        '<h4 class="meili-esim__title">' + escapeHtml(STR.regionalTitle) + '</h4>' +
        '<p class="meili-esim__meta"><strong>' + escapeHtml(reg.product_title) + '</strong> — ' + escapeHtml(covers) + '</p>' +
        '<p class="meili-esim__small">' + escapeHtml(STR.instantQr) + '</p>' +
      '</div>' +
      '<div class="meili-esim__buy">' +
        '<span class="meili-esim__price">' + escapeHtml(reg.price) + '</span>' +
        '<button type="button" class="meili-esim__cta" data-meili-action="add-to-cart" data-variant-id="' + escapeAttr(reg.variant_id) + '" data-regional="1">' + ICONS.cart + '<span>' + escapeHtml(STR.addToCart) + '</span></button>' +
      '</div>' +
    '</aside>';
  }

  function renderAfterTimeline() {
    const zone = document.getElementById('meili-after-timeline');
    if (!zone) return;

    let html = '';

    // Good-to-know notes
    if (trip.notes.length) {
      html += '<section class="meili-notes"><h3 class="meili-notes__title">' + ICONS.sparkle + ' ' + escapeHtml(STR.goodToKnow) + '</h3><div class="meili-notes__grid">' +
        trip.notes.map(function(n) {
          return '<div class="meili-notes__item"><span class="meili-notes__context">' + escapeHtml(n.context) + '</span><p>' + escapeHtml(n.text) + '</p></div>';
        }).join('') + '</div></section>';
    }

    // Refine
    html += '<section class="meili-refine" id="meili-refine">' +
      '<h3 class="meili-refine__title">' + escapeHtml(STR.refineTitle) + '</h3>' +
      '<div class="meili-refine__chips">' +
        STR.refineChips.map(function(chip) {
          return '<button type="button" class="meili-refine__chip" data-meili-action="refine-chip" data-instruction="' + escapeAttr(chip) + '">' + escapeHtml(chip) + '</button>';
        }).join('') +
      '</div>' +
      '<form class="meili-refine__form" id="meili-refine-form">' +
        '<input type="text" class="meili-refine__input" id="meili-refine-input" maxlength="200" placeholder="' + escapeAttr(STR.refinePlaceholder) + '">' +
        '<button type="submit" class="meili-refine__send">' + escapeHtml(STR.refineSend) + '</button>' +
      '</form>' +
    '</section>';

    html += '<footer class="meili-output__footer"><span class="meili-output__footer-brand">Meili</span> — ' + escapeHtml(STR.poweredFooter) + '</footer>';

    html += renderModals();

    zone.innerHTML = html;

    const refineForm = document.getElementById('meili-refine-form');
    if (refineForm) {
      refineForm.addEventListener('submit', function(e) {
        e.preventDefault();
        const input = document.getElementById('meili-refine-input');
        const instruction = (input.value || '').trim();
        if (instruction.length >= 3) requestRefine(instruction);
      });
    }

    // Refine only works with a stored trip id
    if (!trip.id) {
      const refineSection = document.getElementById('meili-refine');
      if (refineSection) refineSection.classList.add('hidden');
    }
  }

  function renderHeroActions() {
    const zone = document.getElementById('hero-actions');
    if (!zone) return;
    zone.innerHTML =
      '<button type="button" class="meili-hero__iconbtn" data-meili-action="open-export" title="' + escapeAttr(STR.save) + '" aria-label="' + escapeAttr(STR.save) + '">' + ICONS.download + '</button>' +
      '<button type="button" class="meili-hero__iconbtn" data-meili-action="open-share" title="' + escapeAttr(STR.share) + '" aria-label="' + escapeAttr(STR.share) + '">' + ICONS.share + '</button>';
  }

  function renderModals() {
    return '' +
    '<div class="meili-modal-overlay" id="meili-share-modal" role="dialog" aria-modal="true" aria-label="' + escapeAttr(STR.share) + '">' +
      '<div class="meili-modal">' +
        '<div class="meili-modal__header"><h3>' + escapeHtml(STR.shareVia) + '</h3><button type="button" class="meili-modal__close" data-meili-action="close-share" aria-label="Close">' + ICONS.x + '</button></div>' +
        '<div class="meili-share__grid">' +
          '<button type="button" class="meili-share__btn meili-share__btn--copy" data-meili-action="copy-link">' + ICONS.link + '<span>' + escapeHtml(STR.copyLink) + '</span></button>' +
          '<button type="button" class="meili-share__btn meili-share__btn--wa" data-meili-action="share-whatsapp">WhatsApp</button>' +
          '<button type="button" class="meili-share__btn meili-share__btn--email" data-meili-action="share-email">Email</button>' +
          '<button type="button" class="meili-share__btn meili-share__btn--tw" data-meili-action="share-twitter">X / Twitter</button>' +
        '</div>' +
      '</div>' +
    '</div>' +
    '<div class="meili-modal-overlay" id="meili-export-modal" role="dialog" aria-modal="true" aria-label="' + escapeAttr(STR.save) + '">' +
      '<div class="meili-modal">' +
        '<div class="meili-modal__header"><h3>' + escapeHtml(STR.save) + '</h3><button type="button" class="meili-modal__close" data-meili-action="close-export" aria-label="Close">' + ICONS.x + '</button></div>' +
        '<div class="meili-export__options">' +
          '<button type="button" class="meili-export__option" data-meili-action="export-pdf">' +
            '<span class="meili-export__icon">' + ICONS.fileText + '</span><span class="meili-export__text"><strong>' + escapeHtml(STR.downloadPdf) + '</strong><small>' + escapeHtml(STR.pdfSub) + '</small></span>' +
          '</button>' +
          '<button type="button" class="meili-export__option" data-meili-action="print">' +
            '<span class="meili-export__icon meili-export__icon--dark">' + ICONS.fileText + '</span><span class="meili-export__text"><strong>' + escapeHtml(STR.printIt) + '</strong><small>' + escapeHtml(STR.printSub) + '</small></span>' +
          '</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  // Sticky bar + bottom sheet -----------------------------------------------
  function renderStickyBar() {
    if (!trip.esim || (!trip.esim.recommendations.length && !trip.esim.regional)) return;
    const primary = trip.esim.regional || trip.esim.recommendations[0];
    const count = trip.esim.recommendations.length + (trip.esim.regional ? 1 : 0);
    const label = trip.esim.regional
      ? STR.regionalTitle
      : primary.country + ' eSIM' + (count > 1 ? ' (+' + (count - 1) + ')' : '');

    const bar = el('' +
      '<div class="meili-esim-bar meili-esim-bar--enter" id="meili-esim-bar" hidden>' +
        '<div class="meili-esim-bar__inner">' +
          renderEsimThumb(primary, 40) +
          '<button type="button" class="meili-esim-bar__label" data-meili-action="open-sheet">' +
            '<strong>' + escapeHtml(label) + '</strong>' +
            '<span>' + escapeHtml(primary.data_amount) + ' · ' + escapeHtml(primary.price) + '</span>' +
          '</button>' +
          '<button type="button" class="meili-esim-bar__cta" data-meili-action="add-to-cart" data-variant-id="' + escapeAttr(primary.variant_id) + '">' + ICONS.cart + '<span>' + escapeHtml(STR.addToCart) + '</span></button>' +
        '</div>' +
      '</div>');
    document.body.appendChild(bar);
    bar.addEventListener('animationend', function() { bar.classList.remove('meili-esim-bar--enter'); }, { once: true });

    // Bottom sheet listing all options
    const sheet = el('' +
      '<div class="meili-sheet" id="meili-esim-sheet" hidden>' +
        '<div class="meili-sheet__backdrop" data-meili-action="close-sheet"></div>' +
        '<div class="meili-sheet__panel" role="dialog" aria-modal="true">' +
          '<div class="meili-sheet__grab" aria-hidden="true"></div>' +
          '<h3 class="meili-sheet__title">' + escapeHtml(STR.sheetTitle) + '</h3>' +
          (trip.esim.regional ? sheetRow(trip.esim.regional, true) : '') +
          trip.esim.recommendations.map(function(r) { return sheetRow(r, false); }).join('') +
        '</div>' +
      '</div>');
    document.body.appendChild(sheet);

    // The bar shows whenever no connectivity card is on screen — the offer
    // stays one tap away without ever doubling up with an in-flow card.
    const esimCards = document.querySelectorAll('.meili-esim');
    if (esimCards.length && 'IntersectionObserver' in window) {
      const visible = new Set();
      const io = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        bar.hidden = visible.size > 0;
      }, { threshold: 0.2 });
      esimCards.forEach(function(c) { io.observe(c); });
    } else {
      bar.hidden = false;
    }

    meiliTrack('esim_bar_shown', {
      country: String(primary.country || primary.region || ''),
      variant_id: String(primary.variant_id || ''),
      price: String(primary.price || ''),
      esim_count: count,
    });
  }

  function sheetRow(rec, isRegional) {
    return '<div class="meili-sheet__row">' +
      renderEsimThumb(rec, 44) +
      '<div class="meili-sheet__info"><strong>' + escapeHtml(isRegional ? STR.regionalTitle : rec.country) + '</strong><span>' + escapeHtml(rec.data_amount) + ' / ' + rec.validity_days + ' ' + STR.days + ' · ' + escapeHtml(rec.price) + '</span></div>' +
      '<button type="button" class="meili-sheet__add" data-meili-action="add-to-cart" data-variant-id="' + escapeAttr(rec.variant_id) + '">' + ICONS.cart + '</button>' +
    '</div>';
  }

  function openEsimSheet() {
    const sheet = document.getElementById('meili-esim-sheet');
    if (sheet) { sheet.hidden = false; requestAnimationFrame(function() { sheet.classList.add('open'); }); }
  }
  function closeEsimSheet() {
    const sheet = document.getElementById('meili-esim-sheet');
    if (sheet) { sheet.classList.remove('open'); setTimeout(function() { sheet.hidden = true; }, REDUCED_MOTION ? 0 : 250); }
  }

  // ==========================================================================
  // Rail sync (IntersectionObserver)
  // ==========================================================================
  let railObserver = null;
  function syncRail() {
    if (!('IntersectionObserver' in window)) return;
    if (railObserver) railObserver.disconnect();
    railObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        const city = entry.target.dataset.city;
        document.querySelectorAll('.meili-rail__chip').forEach(function(chip) {
          chip.classList.toggle('active', chip.dataset.city === city);
        });
      });
    }, { rootMargin: '-30% 0px -60% 0px' });
    document.querySelectorAll('.meili-card').forEach(function(card) { railObserver.observe(card); });
  }

  // ==========================================================================
  // Refine
  // ==========================================================================
  async function requestRefine(instruction) {
    if (!trip.id || isSubmitting) return;
    meiliTrack('refine_requested', { instruction: instruction.slice(0, 60), itinerary_id: trip.id });
    showRevisingOverlay(instruction);
    // Preserve request context (countries etc.) for shell rendering
    const payload = {
      itinerary_id: trip.id,
      instruction: instruction,
      locale: LOCALE,
      stream_version: 2,
    };
    // Reset collected content but keep the current DOM until the new header arrives.
    trip.segments = []; trip.notes = []; trip.esim = null; trip.route = [];
    segmentCountrySeen = {};
    const bar = document.getElementById('meili-esim-bar');
    if (bar) bar.remove();
    const sheet = document.getElementById('meili-esim-sheet');
    if (sheet) sheet.remove();
    await runStream(REFINE_ENDPOINT, payload, { kind: 'refine' });
  }

  function showRevisingOverlay(instruction) {
    hideRevisingOverlay();
    const overlay = el('<div class="meili-revising" id="meili-revising">' +
      '<div class="meili-revising__inner">' +
        '<span class="meili-revising__spinner" aria-hidden="true"></span>' +
        '<p>' + escapeHtml(STR.revising) + '</p>' +
        '<p class="meili-revising__instruction">“' + escapeHtml(instruction) + '”</p>' +
      '</div></div>');
    document.body.appendChild(overlay);
  }
  function hideRevisingOverlay() {
    const overlay = document.getElementById('meili-revising');
    if (overlay) overlay.remove();
  }

  // ==========================================================================
  // Restore (?trip=<id>)
  // ==========================================================================
  async function restoreTrip(tripId) {
    try {
      const response = await fetch(ITINERARY_ENDPOINT + tripId);
      if (!response.ok) throw new Error('not found');
      const doc = await response.json();
      if (!doc || !doc.itinerary) throw new Error('bad doc');

      trip.request = doc.request || null;
      selectedCountries = (doc.request && doc.request.countries) || [];
      trip.id = tripId;
      trip.createdAt = doc.created_at || null;

      // Header
      const header = doc.itinerary.header || {};
      trip.title = header.title || 'Your Trip';
      trip.totalDays = header.days || 0;
      trip.style = header.style || '';
      trip.focus = header.focus || [];

      form.classList.add('hidden');
      if (plannerHeader) plannerHeader.classList.add('hidden');
      resultsSection.classList.remove('hidden');
      streamPhase = 'streaming';
      resultsContent.innerHTML = renderShellHtml();

      // Route + segments + notes
      const overview = doc.itinerary.route_overview || {};
      onContentEvent({ type: 'route_overview', content: overview }, { kind: 'restore' });
      (doc.itinerary.day_segments || []).forEach(function(seg) {
        onContentEvent({ type: 'day_segment', content: seg }, { kind: 'restore' });
      });
      (doc.itinerary.meili_notes || []).forEach(function(n) {
        onContentEvent({ type: 'meili_note', content: n }, { kind: 'restore' });
      });
      if (doc.esim) {
        trip.esim = {
          recommendations: doc.esim.recommendations || [],
          justification: doc.esim.justification || '',
          regional: doc.esim.regional_option || null,
        };
      }

      await finishStreaming({ itinerary_id: tripId }, false);
      const status = document.getElementById('hero-status');
      if (status && trip.createdAt) {
        status.textContent = STR.plannedOn + ' ' + new Date(trip.createdAt).toLocaleDateString(LOCALE === 'es' ? 'es-419' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      }
      meiliTrack('trip_restored', { itinerary_id: tripId });
    } catch (err) {
      console.warn('Trip restore failed:', err);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('trip');
        history.replaceState({}, '', url.toString());
      } catch (e) { /* ignore */ }
      showToast(STR.linkExpired);
    }
  }

  // ==========================================================================
  // Actions (event delegation — no inline onclick)
  // ==========================================================================
  function setupDelegation() {
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('[data-meili-action]');
      if (!btn) return;
      const action = btn.dataset.meiliAction;

      switch (action) {
        case 'start-over': startOver(); break;
        case 'toggle-segment': {
          const card = btn.closest('.meili-card');
          if (card) {
            const nowExpanded = !card.classList.contains('expanded');
            card.classList.toggle('expanded', nowExpanded);
            btn.setAttribute('aria-expanded', String(nowExpanded));
          }
          break;
        }
        case 'rail-jump': {
          const city = btn.dataset.city;
          document.querySelectorAll('.meili-rail__chip').forEach(function(chip) { chip.classList.toggle('active', chip === btn); });
          const target = document.querySelector('.meili-card[data-city="' + (window.CSS && CSS.escape ? CSS.escape(city) : city) + '"]');
          if (target) {
            target.classList.add('expanded');
            const head = target.querySelector('.meili-card__head');
            if (head) head.setAttribute('aria-expanded', 'true');
            target.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'center' });
          }
          break;
        }
        case 'esim-alt': {
          const group = btn.closest('.meili-esim');
          if (group) {
            group.querySelectorAll('.meili-esim__alt').forEach(function(a) { a.classList.toggle('selected', a === btn); });
            const priceEl = group.querySelector('[data-esim-price]');
            if (priceEl) priceEl.textContent = btn.dataset.price || '';
            const cta = group.querySelector('[data-meili-action="add-to-cart"]');
            if (cta) cta.dataset.variantId = btn.dataset.variant || '';
          }
          break;
        }
        case 'add-to-cart': addToCart(btn, btn.dataset.variantId); break;
        case 'open-share': toggleModal('meili-share-modal', true); break;
        case 'close-share': toggleModal('meili-share-modal', false); break;
        case 'open-export': toggleModal('meili-export-modal', true); break;
        case 'close-export': toggleModal('meili-export-modal', false); break;
        case 'export-pdf': exportPdf(btn); break;
        case 'print': toggleModal('meili-export-modal', false); window.print(); break;
        case 'copy-link': copyShareLink(btn); break;
        case 'share-whatsapp': shareWhatsApp(); break;
        case 'share-email': shareEmail(); break;
        case 'share-twitter': shareTwitter(); break;
        case 'refine-chip': requestRefine(btn.dataset.instruction || ''); break;
        case 'open-sheet': openEsimSheet(); break;
        case 'close-sheet': closeEsimSheet(); break;
      }
    });
  }

  function toggleModal(id, open) {
    const modal = document.getElementById(id);
    if (modal) modal.classList.toggle('active', open);
  }

  function startOver() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('trip');
      history.pushState({}, '', url.toString());
    } catch (e) { /* ignore */ }

    form.reset();
    selectedCountries = [];
    updateSelectedDisplay();
    updateStartingCountryOptions();
    updateStartingCityOptions('');
    priorityCheckboxes.forEach(function(cb) { cb.disabled = false; cb.checked = false; });
    if (prioritiesCounter) prioritiesCounter.textContent = '0/2 ' + (LOCALE === 'es' ? 'seleccionadas' : 'selected');
    document.querySelectorAll('.meili-form__error').forEach(function(elx) { elx.classList.add('hidden'); });

    resetTripState();
    streamPhase = 'idle';
    resultsSection.classList.add('hidden');
    resultsContent.innerHTML = '';
    errorSection.classList.add('hidden');
    form.classList.remove('hidden');
    if (plannerHeader) plannerHeader.classList.remove('hidden');
    window.scrollTo({ top: 0, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
  }

  // ==========================================================================
  // Cart
  // ==========================================================================
  async function addToCart(buttonEl, variantId) {
    if (!variantId) { window.location.href = '/collections/esim'; return; }

    let numericId = variantId.indexOf('gid://') > -1 ? variantId.split('/').pop() : variantId;
    numericId = String(numericId).trim();
    if (!/^\d+$/.test(numericId)) { window.location.href = '/collections/esim'; return; }

    const originalHtml = buttonEl.innerHTML;
    buttonEl.disabled = true;
    buttonEl.innerHTML = ICONS.cart + '<span>' + escapeHtml(STR.adding) + '</span>';

    try {
      const cartBefore = await fetch('/cart.js').then(function(r) { return r.json(); });
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: numericId, quantity: 1 }),
      });
      const responseData = await response.json();
      if (!response.ok) throw new Error(responseData.message || responseData.description || ('HTTP ' + response.status));
      if (!responseData.variant_id) throw new Error('Cart response did not contain added item');
      const cartAfter = await fetch('/cart.js').then(function(r) { return r.json(); });
      if (cartAfter.item_count <= cartBefore.item_count) throw new Error('Item was not added to cart');

      buttonEl.classList.add('added');
      buttonEl.innerHTML = ICONS.check + '<span>' + escapeHtml(STR.added) + '</span>';

      meiliTrack('esim_add_to_cart', {
        variant_id: String(numericId),
        product_id: String(responseData.product_id || ''),
        source: 'meili_planner',
      });

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: responseData,
          sourceId: 'meili-esim',
          data: { didError: false, source: 'meili-esim', itemCount: 1, productId: String(responseData.product_id || ''), variantId: numericId },
        },
      }));

      setTimeout(function() {
        buttonEl.classList.remove('added');
        buttonEl.disabled = false;
        buttonEl.innerHTML = originalHtml;
      }, 3000);
    } catch (err) {
      console.error('[Meili] Add to cart error:', err.message);
      meiliTrack('error', { stage: 'add_to_cart', message: String(err && err.message || err).slice(0, 100) });
      buttonEl.disabled = false;
      buttonEl.innerHTML = originalHtml;
      buttonEl.classList.add('error');
      setTimeout(function() { buttonEl.classList.remove('error'); }, 2000);
      showToast(LOCALE === 'es' ? 'No se pudo añadir — inténtalo desde la tienda.' : "Couldn't add — try from the shop page.");
    }
  }

  // ==========================================================================
  // Share
  // ==========================================================================
  function shareUrl() {
    if (trip.id) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('trip', trip.id);
        return url.toString();
      } catch (e) { /* fall through */ }
    }
    return 'https://' + SITE_DOMAIN + '/pages/meili';
  }

  function copyShareLink(btn) {
    const url = shareUrl();
    const done = function() {
      const orig = btn.innerHTML;
      btn.innerHTML = ICONS.check + '<span>' + escapeHtml(STR.copied) + '</span>';
      meiliTrack('share_link_copied', { itinerary_id: trip.id || '' });
      setTimeout(function() { btn.innerHTML = orig; }, 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(function() { window.prompt('Copy link:', url); });
    } else {
      window.prompt('Copy link:', url);
    }
  }

  function shareWhatsApp() {
    let msg = '*' + trip.title + '*\n' + trip.totalDays + ' ' + STR.days + '\n\n';
    trip.segments.forEach(function(seg) {
      msg += '*' + seg.city + '*' + (seg.daysLabel ? ' (' + seg.daysLabel + ')' : '') + '\n';
      seg.highlights.slice(0, 3).forEach(function(h) { msg += '• ' + h + '\n'; });
      msg += '\n';
    });
    msg += shareUrl();
    window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank');
    meiliTrack('share', { channel: 'whatsapp', itinerary_id: trip.id || '' });
  }

  function shareEmail() {
    let email = trip.title.toUpperCase() + '\n' + trip.totalDays + ' ' + STR.days + '\n\n';
    trip.route.forEach(function(stop, i) {
      email += (i + 1) + '. ' + stop.city + (stop.nights ? ' (' + stop.nights + ' ' + pluralNights(stop.nights) + ')' : '') + '\n';
    });
    email += '\n';
    trip.segments.forEach(function(seg) {
      email += '>> ' + seg.city.toUpperCase() + (seg.daysLabel ? ' — ' + seg.daysLabel : '') + '\n';
      seg.days.forEach(function(d) {
        email += '   ' + STR.day + ' ' + d.day + (d.title ? ': ' + d.title : '') + '\n';
        d.activities.forEach(function(a) { email += '     - ' + a + '\n'; });
      });
      email += '\n';
    });
    email += shareUrl() + '\n\nMeili — ' + BRAND_NAME + ' | ' + SITE_DOMAIN + '\n';
    const subject = trip.title;
    window.open('mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(email), '_self');
    meiliTrack('share', { channel: 'email', itinerary_id: trip.id || '' });
  }

  function shareTwitter() {
    const cities = trip.route.map(function(s) { return s.city; }).join(' → ');
    let text = trip.title + ' (' + trip.totalDays + ' ' + STR.days + ')';
    if (cities) text += '\n' + cities;
    text += '\n\n' + shareUrl() + '\nvia @' + TWITTER_HANDLE;
    window.open('https://twitter.com/intent/tweet?text=' + encodeURIComponent(text), '_blank');
    meiliTrack('share', { channel: 'twitter', itinerary_id: trip.id || '' });
  }

  // ==========================================================================
  // PDF export (html2pdf lazy-loaded on demand)
  // ==========================================================================
  let html2pdfLoading = null;
  function loadHtml2Pdf() {
    if (window.html2pdf) return Promise.resolve();
    if (html2pdfLoading) return html2pdfLoading;
    html2pdfLoading = new Promise(function(resolve, reject) {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      s.integrity = 'sha512-GsLlZN/3F2ErC5ifS5QtgpiJtWd43JWSuIgh7mbzZ8zBps+dvLusV+eNQATqgA/HdeKFVgA5v3S/cIrLF7QnIg==';
      s.crossOrigin = 'anonymous';
      s.referrerPolicy = 'no-referrer';
      s.onload = resolve;
      s.onerror = function() { html2pdfLoading = null; reject(new Error('html2pdf load failed')); };
      document.head.appendChild(s);
    });
    return html2pdfLoading;
  }

  function buildPdfHtml() {
    let pdfHtml = '<div style="font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,sans-serif;color:#1a1a1a;line-height:1.5;max-width:700px;margin:0 auto;">' +
      '<div style="background:#14203A;color:#fff;padding:28px;margin-bottom:20px;border-radius:10px;">' +
        '<div style="color:#F97316;font-size:13px;font-weight:700;margin-bottom:6px;">Meili</div>' +
        '<h1 style="margin:0 0 6px 0;font-size:26px;font-weight:700;">' + escapeHtml(trip.title) + '</h1>' +
        '<p style="margin:0;opacity:.85;font-size:14px;">' + trip.totalDays + ' ' + STR.days + (trip.style ? ' · ' + escapeHtml(trip.style) : '') + '</p>' +
      '</div>';

    if (trip.route.length) {
      pdfHtml += '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:22px;">';
      trip.route.forEach(function(stop, i) {
        pdfHtml += '<span style="background:#F4F1EC;color:#333;padding:6px 14px;border-radius:20px;font-size:13px;">' + (i + 1) + '. ' + escapeHtml(stop.city) + (stop.nights ? ' · ' + stop.nights + ' ' + pluralNights(stop.nights) : '') + '</span>';
      });
      pdfHtml += '</div>';
    }

    trip.segments.forEach(function(seg) {
      pdfHtml += '<div style="border:1px solid #e5e5e5;border-radius:12px;padding:18px 20px;margin-bottom:14px;page-break-inside:avoid;">' +
        '<div style="display:flex;align-items:center;gap:12px;margin-bottom:10px;">' +
          '<span style="background:#14203A;color:#fff;padding:8px 12px;border-radius:8px;font-weight:600;font-size:13px;white-space:nowrap;">' + seg.nights + ' ' + pluralNights(seg.nights) + '</span>' +
          '<div><h3 style="margin:0;font-size:17px;">' + escapeHtml(seg.city) + ' <span style="font-weight:normal;color:#666;font-size:13px;">' + escapeHtml(seg.daysLabel) + '</span></h3>' +
          '<p style="margin:2px 0 0 0;color:#666;font-size:13px;">' + escapeHtml(seg.subtitle) + '</p></div>' +
        '</div>';
      seg.days.forEach(function(d) {
        pdfHtml += '<div style="margin-bottom:10px;padding-left:12px;border-left:2px solid #F97316;">' +
          '<strong style="color:#14203A;font-size:13px;">' + STR.day + ' ' + d.day + '</strong> <span style="color:#444;font-size:13px;">' + escapeHtml(d.title) + '</span>';
        if (d.activities.length) {
          pdfHtml += '<ul style="margin:5px 0 0 0;padding-left:18px;font-size:12.5px;color:#444;">';
          d.activities.forEach(function(a) { pdfHtml += '<li style="margin-bottom:2px;">' + escapeHtml(a) + '</li>'; });
          pdfHtml += '</ul>';
        }
        if (d.note) pdfHtml += '<div style="margin-top:4px;font-size:12px;color:#C2560F;font-style:italic;">★ ' + escapeHtml(d.note) + '</div>';
        pdfHtml += '</div>';
      });
      if (seg.transport && seg.transport.destination) {
        const t = seg.transport;
        pdfHtml += '<div style="background:#F8F6F2;padding:10px 14px;border-radius:8px;margin-top:8px;font-size:13px;">' +
          '<strong>' + escapeHtml(STR.nextStop) + ': ' + escapeHtml(t.destination) + '</strong> <span style="color:#666;">' + escapeHtml(t.mode || '') + (t.duration ? ' · ' + escapeHtml(t.duration) : '') + '</span>' +
          (t.tip ? '<div style="color:#C2560F;font-size:12px;margin-top:3px;">' + escapeHtml(t.tip) + '</div>' : '') +
        '</div>';
      }
      pdfHtml += '</div>';
    });

    if (trip.notes.length) {
      pdfHtml += '<div style="border-top:1px solid #e5e5e5;padding-top:16px;margin-top:18px;"><h2 style="font-size:16px;margin-bottom:10px;">' + escapeHtml(STR.goodToKnow) + '</h2>';
      trip.notes.forEach(function(n) {
        pdfHtml += '<p style="font-size:13px;color:#444;margin:0 0 8px 0;"><strong>' + escapeHtml(n.context) + ':</strong> ' + escapeHtml(n.text) + '</p>';
      });
      pdfHtml += '</div>';
    }

    pdfHtml += '<div style="text-align:center;color:#999;font-size:11px;margin-top:26px;padding-top:16px;border-top:1px solid #eee;">Meili · ' + escapeHtml(SITE_DOMAIN) + (trip.id ? ' · ' + escapeHtml(shareUrl()) : '') + '</div></div>';
    return pdfHtml;
  }

  function exportPdf(btn) {
    toggleModal('meili-export-modal', false);
    const filename = (trip.title || 'itinerary').replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').toLowerCase() + '.pdf';
    loadHtml2Pdf().then(function() {
      const container = document.createElement('div');
      container.innerHTML = buildPdfHtml();
      return window.html2pdf().set({
        margin: [15, 15, 15, 15],
        filename: filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css'] },
      }).from(container).save();
    }).then(function() {
      meiliTrack('pdf_export', { filename: filename.slice(0, 100) });
    }).catch(function(err) {
      console.error('PDF generation error:', err);
      meiliTrack('error', { stage: 'pdf_export', message: String(err && err.message || err).slice(0, 100) });
      window.print();
    });
  }

  // ==========================================================================
  // Feedback UI (toast, error, a11y announcements)
  // ==========================================================================
  let announceEl = null;
  function announce(text) {
    if (!announceEl) {
      announceEl = el('<div class="meili-sr-only" aria-live="polite"></div>');
      document.body.appendChild(announceEl);
    }
    announceEl.textContent = text;
  }

  function showToast(message) {
    const existing = document.getElementById('meili-toast');
    if (existing) existing.remove();
    const toast = el('<div class="meili-toast" id="meili-toast" role="status">' + escapeHtml(message) + '</div>');
    document.body.appendChild(toast);
    requestAnimationFrame(function() { toast.classList.add('show'); });
    setTimeout(function() {
      toast.classList.remove('show');
      setTimeout(function() { toast.remove(); }, 400);
    }, 4200);
  }

  function showError(message) {
    streamPhase = 'error';
    form.classList.add('hidden');
    if (plannerHeader) plannerHeader.classList.add('hidden');
    resultsSection.classList.add('hidden');
    errorSection.classList.remove('hidden');
    errorMessage.textContent = message;
  }

  function setLoadingState(loading) {
    if (!submitBtn) return;
    submitBtn.disabled = loading;
    if (submitText) submitText.classList.toggle('hidden', loading);
    if (submitLoading) submitLoading.classList.toggle('hidden', !loading);
  }

  // ==========================================================================
  // Boot
  // ==========================================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
